import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, of, Subscription, BehaviorSubject, Observer, combineLatest, throwError, lastValueFrom, forkJoin, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, map, mergeMap, take, filter, tap, distinctUntilChanged, finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import moment from 'moment-timezone';

import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { CommonApiErrorModel } from '../../../api/genericApi/models/common-api-error.model';
import { AbstractPageBuilderService } from '../../PageBuilder/services/abstract-page-builder.service';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { BlockTypeModel } from '../../../Common/PageBuilder/models/block-type.model';
import { ConfirmDialogComponent } from '../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { defaultDistributionStyles,
    newBrandingDefaultDistributionStyles
} from '../../../Discover/Targeting/models/Distribution/default-distribution-styles.model';
import { DeliverySchedule } from '../../Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { DiscoverRouteModel } from '../../../Discover/DiscoverRouter/models/discover-route.model';
import { DiscoverRouteTypeEnum } from '../../../Discover/DiscoverRouter/enums/discover-route-type.enum';
import { DiscoverRoutingService } from '../../../Discover/DiscoverRouter/services/discover-routing.service';
import { DistributionApi } from '../../../api/distributions/distribution.api';
import { DistributionApiModel } from '../../../api/distributions/model/distribution-api.model';
import { distributionBlockToGeneralStyle } from '../../../Discover/Targeting/models/distribution-global-styles-property.const';
import {
    DistributionBlockTypeEnum,
    styleTypeApiEnumToDistributionBlockTypeEnum
} from '../../../Discover/Targeting/enums/distribution-block-type.enum';
import { ResourceBodyApiModel } from '../../../api/distributions/model/distribution-body.api.model';
import { DistributionFactoryModel } from '../../../Discover/Targeting/models/Distribution/distribution-factory.model';
import { DistributionQuotaInfo } from '../../../Common/Charts/models/distribution.quota.info';
import { DistributionRecipientStatus } from '../../../Discover/Targeting/enums/distribution-recipient-status.enum';
import {
    distributionsBlockTypes, distributionFooterBlockType, distributionTextBlockType, distributionLinkBlockType,
    distributionLayoutBlockTypes
} from '../../../Common/TargetingShared/model/distribution-block-types.model';
import { DistributionModel } from '../../../Discover/Targeting/models/distribution.model';
import { DistributionStyleStateEnum } from '../../../Common/TargetingShared/enum/distribution-style-state.enum';
import { DistributionSummaryModel } from '../../../Common/TargetingShared/model/distribution-summary-model';
import { FooterDisplayElementModel } from '../../../Common/TargetingShared/model/footer-display-element.model';
import { FooterModel } from '../../../Common/TargetingShared/model/footer.model';
import { GenericStyleModel } from '../../../Common/Styles/models/generic-style.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MergeTagApiModel } from '../../../api/distributions/model/merge-tag.api.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { DistributionPlainTextService } from '../../../Discover/Targeting/components/Distribution/PlainTextEditorDialog/plain-text.service';
import { RecipientGroup } from '../../../Discover/Targeting/models/recipient-group.model';
import { RecipientInformationModel } from '../../../Discover/Targeting/models/recipient-information.model';
import { RichTextDisplayElementModel } from '../../../Common/PageBuilder/models/rich-text-display-element.model';
import { ScheduleType } from '../../../Common/Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { StylesService } from '../../../Common/Styles/services/styles.service';
import { StyleStateEnum } from '../../../Common/PageBuilder/enums/style-state.enum';
import { TargetingListList } from '../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingListService } from '../../../Discover/Targeting/services/targeting-list.service';
import { TargetingPreferencesService } from '../../../Common/TargetingShared/services/targeting-preferences.service';
import { TargetingSearchesService } from '../../../Discover/Targeting/services/targeting-searches.service';
import { TargetingSearchList } from '../../../Discover/Targeting/models/targeting-search-list.model';
import { Template } from '../../../Common/Styles/models/template.model';
import { TwigRendererService } from '../../../Common/PageBuilder/services/twig-renderer.service';
import { UserModel } from '../../../Core/Users/models/user.model';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { validateEmail } from '../../../Common/Misc/helpers/formatting/email.helper';
// eslint-disable-next-line @typescript-eslint/naming-convention
import Twig from '../../../Common/PageBuilder/helpers/twig.extensions';
import { amplificationStatusFromApi } from '../../../Discover/Targeting/enums/amplification-status.enum';
import { AmplificationStatusAPIEnum } from '../../../api/distributions/enum/amplification-status-api.enum';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { ButtonDisplayElementModel } from '../../../Common/PageBuilder/models/button-display-element.model';
import { ImageDisplayElementModel } from '../../../Common/PageBuilder/models/image-display-element.model';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { FilterListProviderServiceInterface } from '../../../Common/Filter/services/filter-list-provider-service.interface';
import { DistributionStatus } from '../../../Common/TargetingShared/enum/distribution-status.enum';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';

import { DistributionAccountStatisticsDateRange } from '../../../api/distributions/model/distribution-account-statistics-date-range.api.model';
import { DistributionAccountStatisticsModel } from '../model/statisticsReport/distribution-account-statistics.model';
import { EmailHelper } from '../../../Common/PageBuilder/helpers/email.helper';
import { DistributionRecipientReportApiModel, DistributionReportStat } from '../model/statisticsReport/distribution-report-stat-api.model';
import { ExcludedRecipientApiModel } from '../../../api/distributions/model/excluded-recipient.api.model';
import { UserAppTargetingPreferencesModel } from '../../../Core/Users/models/user-app-preferences.model';
import { PortfolioService } from '../../../Discover/Targeting/components/Portfolios/portfolio-service';
import { PortfolioModel } from '../../../Discover/Targeting/components/Portfolios/portfolio.model';
import { RecipientType } from '../../../Discover/Targeting/enums/recipient-type.enum';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { DistributionSummaryApiModel } from '../../../api/distributions/model/distribution-summary-api.model';
import { LinkDisplayElementModel } from '../../../Common/PageBuilder/models/link-display-element.model';
import { newBrandingMarketingDistribution, newBrandingPressRelease } from '../../../Discover/Targeting/models/Distribution/distribution-factory-templates';
import { DistributionType } from '../enum/distribution-type.enum';
import { AiDistributionContent } from '../../../Discover/Targeting/components/Distribution/AIQuickDistributionDialog/ai-quick-distribution-dialog.component';
import { CurrentResourceInterface } from '../../Basic/models/current-resource-abstract.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { StatusLhpItem } from '../../../Common/Layout/services/left-sidebar-management.service';
import { MediaServiceAccessTypeEnum } from '@api/mediaService/enums/media-service-access-type.enum';
import { FileUploadModel } from '@Common/Forms/models/file-upload.model';
import { PendoService } from '@Common/Misc/services/pendo.service';
import { PendoEvent } from '@Common/Misc/models/pendo-event';
import { MediaServiceApi } from '@api/mediaService/media-service.api';
import { MediaServiceErrorCodesEnum } from '@api/mediaService/mediaResults.api.model';
import { MergeTagModel } from '@Discover/Core/models/merge-tag.model';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { TargetingQueryApi } from '../../../api/targeting/model/targeting-search.api.model';
import { DisplayGroupModel } from '../../Layout/models/display-group.model';
import { PersonalizationTagEnum } from '@Discover/Targeting/enums/personalization-tag.enum';
import { TargetingSearchService } from '@Discover/Targeting/services/targeting-search.service';
import { LayoutBlock } from '../../../Common/PageBuilder/models/layout-block.model';
import { LayoutBlockDisplayRatiosEnum } from '../../../Common/PageBuilder/enums/layout-block-display-ratios.enum';
import { LayoutDisplayElement } from '../../../Common/PageBuilder/models/layout-display-element.model';
import { BlockMoveDirectionEnum } from '../../../Common/PageBuilder/enums/direction.enum';
import { FocusedMoveButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { RecipientSearchModel } from '@Discover/Targeting/components/Distribution/DistributionReport/RecipientSearch/recipient-search.model';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { SortFieldEnum } from '../enum/SortField.enum';
import { TargetingSearch } from '@Discover/Targeting/models/targeting-search.model';
import { LayoutBlockType } from '../../../Common/PageBuilder/models/layout-block-type.model';
import { DataConnectionService } from '../../../Core/Users/services/data-connection.service';
import { EmailAccountConnectorApi } from '../../../api/emailAccountConnector/models/email-account-connector.api';
import { DistributionSendFrom } from '../../../Discover/Targeting/models/distribution-send-from.model';
import { gateCall } from '@Core/rxjs-utils';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';
import { DisplayGroupsService } from '@Common/Layout/services/display-groups.service';
import { AEORecommendationModel, AEORecommendationsApiModel } from '../../../api/distributions/model/aeo-recommendations.api.model';
import { RecentColorsService } from '../../../Common/Forms/components/AdvancedColorPickerPopover/service/recent-colors.service';

export class DistributionRenderOptions {
    isTestEmail?: boolean = false;
    hideViewInBrowserUrl?: boolean = false;
    disableViewInBrowserLink?: boolean = false;
}

export const enum DistributionValidationLevel {
    DRAFT,
    SEND,
    SENT,
    SENDTESTEMAIL
}
export class DistributionErrors {
    hasNoName: boolean = false;
    nameExists: boolean = false;
    hasNoRecipients: boolean = false;
    exceedsQuota: boolean = false;
    deliveryScheduleInvalid: boolean = false;
    headerHasNoSubjectLine: boolean = false;
    headerHasNoFrom: boolean = false;
    headerHasNoReplyTo: boolean = false;
    headerHasInvalidEmail: boolean = false;
    footerHasNoCompany: boolean = false;
    footerHasNoStreetAddress: boolean = false;
    footerHasNoCity: boolean = false;
    footerHasNoCountry: boolean = false;
    footerHasNoZIP: boolean = false;
    footerHasNoContact: boolean = false;
    footerHasInvalidEmail: boolean = false;
    invalidBlocks: boolean = false;
    emptyBlocks: boolean = false;
    footerMissingMergeTags: boolean = false;
    sendFromNeedsReAuth: boolean = false;
    missingTags: string[] = [];

    getErrorCount(): number {
        let errorCount = Object.values(this).filter(Boolean).length;
        if (this.missingTags.length === 0) {
            errorCount--;
        }

        return errorCount;
    }

    isHeaderValid(): boolean {
        return !(this.headerHasNoSubjectLine || this.headerHasNoFrom || this.headerHasNoReplyTo || this.headerHasInvalidEmail);
    }
}

@Injectable()
export class DistributionService extends AbstractPageBuilderService
    implements OnDestroy, FilterListProviderServiceInterface, CurrentResourceInterface {

    loading: boolean = false;
    private loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.loading);

    testEmailSent: boolean = false;
    private testEmailSent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.testEmailSent);

    protected subscriptions: Subscription[] = [];

    private distributionSummaries: DistributionSummaryModel[] = null;
    private distributionSummaries$: BehaviorSubject<DistributionSummaryModel[]> = new BehaviorSubject<DistributionSummaryModel[]>(this.distributionSummaries);

    private cachedDistribution: DistributionModel;
    private focusedDistribution: DistributionModel;
    private focusedDistribution$: BehaviorSubject<DistributionModel> = new BehaviorSubject<DistributionModel>(null);

    private distributionDirty: boolean;
    private distributionDirty$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private currentRoute: DiscoverRouteModel;
    private currentUser: UserModel;
    private distributionQuotaInfo: DistributionQuotaInfo;

    // To update distribution recipients with the appropriate labels and counts
    private listSummaries: TargetingListList[] = null;
    private searchSummaries: TargetingSearchList[] = null;
    private portfolios: PortfolioModel[] = null;

    // The backend returned the IDs only.
    // We flesh out the labels and the counts
    private recipientCountSubscription: Subscription;

    private cachedFooter: FooterModel;
    private defaultFooterDirty$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private userDistributionDetails: UserAppTargetingPreferencesModel['distributionsPrefs']['defaultDistributionDetails'];
    private defaultRecipients: UserAppTargetingPreferencesModel['distributionsPrefs']['defaultRecipients'];

    private validationErrors$: BehaviorSubject<DistributionErrors> = new BehaviorSubject<DistributionErrors>(null);

    public defaultStyles = defaultDistributionStyles;

    private availableMergeTags: MergeTagModel[] = [
        // NEP-18499 new MergeTagModel({ key: PersonalizationTagEnum.NAME,        fallback: null}),
        new MergeTagModel({ key: PersonalizationTagEnum.PREFIX,      fallback: null}),
        new MergeTagModel({ key: PersonalizationTagEnum.FNAME,       fallback: null}),
        new MergeTagModel({ key: PersonalizationTagEnum.LNAME,       fallback: null}),
        new MergeTagModel({ key: PersonalizationTagEnum.OUTLET_NAME, fallback: null}),
        new MergeTagModel({ key: PersonalizationTagEnum.X_HANDLE,    fallback: null}),
        new MergeTagModel({ key: PersonalizationTagEnum.INTRO,       fallback: null}),
        new MergeTagModel({ key: PersonalizationTagEnum.OUTRO,       fallback: null}),
    ];
    private availableMergeTags$: BehaviorSubject<MergeTagModel[]> = new BehaviorSubject<MergeTagModel[]>(this.availableMergeTags);

    private emailAccountConnectors: EmailAccountConnectorApi[];

    /* eslint-disable @typescript-eslint/naming-convention */
    private readonly SampleTestEmailTagDefaultValues = {
        '[%PREFIX%]': 'Mr.',
        '[%FNAME%]': 'John',
        '[%LNAME%]': 'Doe',
        '[%OUTLET_NAME%]': 'Sample Outlet',
        '[%CITY%]': 'Cityville',
        '[%TWITTER_HANDLE%]': '@samplehandle',
        '[%X_HANDLE%]': '@samplehandle',
        '[%NAME%]': 'John Doe',
        '[%INTRO%]': 'Sample Intro',
        '[%OUTRO%]': 'Sample Outro'
    };

    /* eslint-disable @typescript-eslint/naming-convention */
    sampleTestEmailTagValues = JSON.parse(JSON.stringify(this.SampleTestEmailTagDefaultValues));

    footerMergeTags: string[] = [
        '[%COMPANY_ORGANIZATION%]',
        '[%STREET_ADDRESS%]',
        '[%CITY%]',
        '[%COUNTRY%]',
        '[%ZIP_POSTAL_CODE%]',
        '[%CONTACT_INFORMATION%]',
        '[%UNSUBSCRIBE%]'
    ];

    private pendoCreateData: PendoEvent = null;
    private defaultSendFromConnector: EmailAccountConnectorApi;

    private AEORecommendations$: BehaviorSubject<AEORecommendationModel[]> = new BehaviorSubject<AEORecommendationModel[]>([]);
    private AEORecommendationsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private discoverRoutingService: DiscoverRoutingService,
        private distributionApi: DistributionApi,
        private messageService: MessageService,
        private coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        private targetingListService: TargetingListService,
        private targetingSearchesService: TargetingSearchesService,
        private targetingPreferencesService: TargetingPreferencesService,
        private targetingListsApiService: TargetingListsApiService,
        private userPreferencesService: UserPreferencesService,
        public stylesService: StylesService,
        public twigRenderService: TwigRendererService,
        public plainTextService: DistributionPlainTextService,
        private websocketEvents: WebsocketAwareNeptuneService,
        private pendoService: PendoService,
        public portfolioService: PortfolioService,
        public configurationService: ConfigService,
        private mediaServiceApi: MediaServiceApi,
        private targetingSearchService: TargetingSearchService,
        private dataConnectionService: DataConnectionService,
        private displayGroupsService: DisplayGroupsService,
        private router: Router,
        private recentColorsService: RecentColorsService
    ) {
        super(stylesService, twigRenderService, localizationService);

        if (this.configurationService.isNewBranding()) {
            DistributionFactoryModel.defaultStyles = newBrandingDefaultDistributionStyles;
            DistributionFactoryModel.marketingDistribution = newBrandingMarketingDistribution;
            DistributionFactoryModel.pressRelease = newBrandingPressRelease;
        }

        this.defaultStyles = DistributionFactoryModel.defaultStyles;
        this.setInlineAdderHeight(276);

        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    if (
                        route.value &&
                        (this.currentRoute ? route.value !== this.currentRoute.value : true) &&
                        (route.type === DiscoverRouteTypeEnum.distributionEdit || route.type === DiscoverRouteTypeEnum.distributionReport)
                    ) {
                        this.setCurrentDistributionFromApi(+route.value);
                    }

                    this.currentRoute = route;

                    // If reaching /distribution on initial page load, the user will be redirected to distribution hype page
                    if (route.type === DiscoverRouteTypeEnum.distributionNew) {
                        const newDistributionContentKey = this.router.parseUrl(this.router.url).queryParamMap.get('newDistributionContentKey');
                        if (!this.focusedDistribution && !newDistributionContentKey) {
                            this.discoverRoutingService.gotoDistributionHype();
                        }
                    }
                }
            ),
            this.targetingListService.all$.pipe(
                filter(x => !!x))
                .subscribe(lists => {
                    this.listSummaries = lists;
                    // we want to check the recipient counts whenvever websockets are called, so we will fleshOutRecipientInformation everytime it is returned
                    this.fleshOutRecipientInformation();
                }),
            this.portfolioService.getPortfolios().pipe(
                filter(x => !!x))
                .subscribe(portfolios => {
                    let firstLoad = false;
                    if (!this.portfolios) {
                        // we just landed on a distribution, so it's possible that it was not able to flesh out its recipient info
                        // fleshOutRecipientInformation should not be called every time the list of lists changes, it should be called every time
                        // the distribution changes but should wait for list of lists to have a value after the initial load of the page,
                        // in this situation, we can call it from here to make sure that the recipients do get fleshed out
                        firstLoad = true;
                    }
                    this.portfolios = portfolios;
                    if (firstLoad) {
                        this.fleshOutRecipientInformation();
                    }
                }),
            this.targetingSearchesService.all$.pipe(
                filter(x => !!x))
                .subscribe(searches => {
                    this.searchSummaries = searches;
                    // we want to check the recipient counts whenvever websockets are called, so we will fleshOutRecipientInformation everytime it is returned
                    this.fleshOutRecipientInformation();
                }),
            this.targetingPreferencesService.distributionQuotaInfo$.pipe(filter(x => !!x)).subscribe(quota => this.distributionQuotaInfo = quota),
            this.hasDefaultTemplates$.subscribe(
                hasTemplates => {
                    if (hasTemplates) {
                        this.setBlockTypes(this.setDefaultTemplatesOnBlockTypes(distributionsBlockTypes));
                        this.setDefaultTemplatesOnBlockTypes(distributionLayoutBlockTypes);
                        this.setTemplatesOnBlocks(this.blocks);
                    }
                }
            ),
            this.currentUserStateService.getUser().pipe(filter(x => !!x)).subscribe(user => this.currentUser = user),
            this.userPreferencesService.getDistributionDefaultFooter().subscribe(defaultFooter => {
                this.cachedFooter = defaultFooter.clone();
            }),
            this.reloadDistributionSummaries(),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x))
                .subscribe(
                    message => {
                        if (message.resourceType === ResourceType.distributions) {
                            if (
                                message?.slug && (
                                    (message.slug as string).includes('recipientreport') ||
                                    (message.slug as string).includes('accountStats') ||
                                    (message.slug as string).includes('searchrecipients') ||
                                    (message.slug as string).includes('searchadditionalrecipients') ||
                                    (message.slug as string).includes('eventsovertime') ||
                                    (message.slug as string).includes('quotaovertime') ||
                                    (message.slug as string).includes('linksreport') ||
                                    (message.slug as string).includes('export') ||
                                    (message.slug as string).includes('addrecipients') ||
                                    (message.slug as string).includes('removerecipients') ||
                                    (message.slug as string).includes('defaultfooter') ||
                                    (message.slug as string).includes('distributionPostOptions') ||
                                    (message.slug as string).includes('accountStats') ||
                                    (message.slug as string).includes('addrecipientlist') ||
                                    (message.slug as string).includes('testemail')
                                )
                            ) {
                                // report events won't cause changes in the LHP so no need to reload
                            } else {
                                this.reloadDistributionSummaries();
                            }
                        }
                    }
                ),
            this.userPreferencesService.getUserAppPreferences().subscribe((userAppPreferences) => {
                this.userDistributionDetails = userAppPreferences.targetingPrefs.distributionsPrefs.defaultDistributionDetails;
                this.defaultRecipients = userAppPreferences.targetingPrefs.distributionsPrefs.defaultRecipients;
            }),
            gateCall(this.dataConnectionService.currentUserHasEmailAccountConnectorAccessOnEnvironment()).pipe(
                switchMap(() => combineLatest([this.userPreferencesService.getUserAppPreferences(), this.dataConnectionService.emailAccountConnectors$]))
            ).subscribe(([userAppPreferences, emailAccountConnectors]) => {
                this.emailAccountConnectors = emailAccountConnectors;
                if (userAppPreferences.targetingPrefs.distributionsPrefs.defaultEmailAccount) {
                    emailAccountConnectors?.forEach(item => {
                        if (item.id === userAppPreferences.targetingPrefs.distributionsPrefs.defaultEmailAccount) {
                            this.defaultSendFromConnector = item;
                        }
                    });
                }
            })
        );

        distributionFooterBlockType.showInBlockAdder = false;

        this.setBlockTypeGroups([
            {
                blockTypes: distributionsBlockTypes
            }
        ]);

        this.setLayoutBlockTypes(distributionLayoutBlockTypes);

        this.setBlockTypeEnumFromStyleTypeEnumMap(styleTypeApiEnumToDistributionBlockTypeEnum);
        this.setBlockToGeneralStylePropertiesMap(distributionBlockToGeneralStyle);
    }

    setCurrentDistributionFromApi(distributionId: number): void {
        combineLatest([
            this.distributionApi.getById(distributionId).pipe(
                filter(x => !!x),
                take(1)
            ),
            this.hasDefaultTemplates().pipe(
                filter(x => !!x),
                take(1)
            ),
            this.distributionApi.getDistributionPostOptions(distributionId).pipe(filter(x => !!x)),
            this.stylesService.all$.pipe(
                filter(x => !!x),
                take(1)
            ),
            this.dataConnectionService.currentUserHasEmailAccountConnectorAccessOnEnvironment().pipe(
                switchMap(hasAccess => {
                    if (hasAccess) {
                        return this.dataConnectionService.emailAccountConnectors$;
                    }
                    return of([]);
                })
            ),
            this.currentUserStateService.getUser()
        ]).pipe(
            catchError(error => {
                if(error.status === 404) {
                    this.coreDialogService.open(ConfirmDialogComponent,{
                        inputs:{
                            heading:'Monitor.results.distributionNotFoundDialog.heading',
                            message:'Monitor.results.distributionNotFoundDialog.message',
                            okLabel:'Monitor.results.distributionNotFoundDialog.acknowledge',
                            showCancel:false,
                            confirmAction: () => {
                                this.discoverRoutingService.gotoDistributionHype();
                                return of(true);
                            }
                        }
                    });
                }

                if(error.status === 403) {
                    this.coreDialogService.open(ConfirmDialogComponent,{
                        inputs:{
                            heading:'Discover.distribution.distributionNotPermitted.heading',
                            message:'Discover.distribution.distributionNotPermitted.message',
                            okLabel:'Discover.distribution.distributionNotPermitted.acknowledge',
                            showCancel:false,
                            confirmAction: () => {
                                this.discoverRoutingService.gotoDistributionHype();
                                return of(true);
                            }
                        }
                    });
                }
                throw error;
            })
        ).subscribe(([distributionApiModel, hasDefaultTemplates, distributionPostOptions, styles, emailAccountConnectors, user]) => {
            if (distributionApiModel.sendFrom?.connectionEmail) {

                const emailConnector: EmailAccountConnectorApi
                    = emailAccountConnectors.find(x => x.connectorEmail === distributionApiModel.sendFrom.connectionEmail);

                if (emailConnector?.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.PRIVATE && emailConnector.userId !== user.id) {
                    this.coreDialogService.open(ConfirmDialogComponent,{
                        inputs:{
                            heading:'Discover.distribution.distributionNotPermitted.heading',
                            message:'Discover.distribution.distributionNotPermitted.message',
                            okLabel:'Discover.distribution.distributionNotPermitted.acknowledge',
                            showCancel:false,
                            confirmAction: () => {
                                this.discoverRoutingService.gotoDistributionHype();
                                return of(true);
                            }
                        }
                    });

                    return;
                }
            }

            const currentDistribution = new DistributionModel(distributionApiModel);

            if (distributionApiModel.generalStyles === '') {
                currentDistribution.generalStyles.setStyles(this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_1]);
            } else {
                if (currentDistribution.generalStyles[DistributionBlockTypeEnum.link].isSavedStyle()) {
                    const savedLinkStyle = styles.find(x => x.id === currentDistribution.generalStyles[DistributionBlockTypeEnum.link].styleState);
                    if (savedLinkStyle) {
                        currentDistribution.generalStyles[DistributionBlockTypeEnum.link].setFromApiModel(savedLinkStyle);
                    }
                }
                if (currentDistribution.generalStyles[DistributionBlockTypeEnum.button].isSavedStyle()) {
                    const savedButtonStyle = styles.find(x => x.id === currentDistribution.generalStyles[DistributionBlockTypeEnum.button].styleState);
                    if (savedButtonStyle) {
                        currentDistribution.generalStyles[DistributionBlockTypeEnum.button].setFromApiModel(savedButtonStyle);
                    }
                }
            }
            if (distributionPostOptions?.length > 0) {
                const amplifications = [];
                distributionPostOptions.forEach(amplification => amplifications.push(amplificationStatusFromApi.get(amplification)));
                currentDistribution.amplification = amplifications;
            }

            this.setEmailAccountConnectorName(emailAccountConnectors, currentDistribution);
            this.setFocusedDistribution(currentDistribution);
        });
    }

    private setEmailAccountConnectorName(emailConnectors: EmailAccountConnectorApi[], distribution: DistributionModel): void {
        if (emailConnectors?.length) {
            emailConnectors.forEach(item => {
                if (distribution.sendFrom?.connectionId === item.id) {
                    distribution.sendFrom.connectionName = item.connectorName;
                }
            });
        }
    }

    setFocusedDistribution(distribution: DistributionModel, fetchRecipientInfo: boolean = true) {
        if (distribution === null) {
            this.focusedDistribution$.next(null);
            return;
        }

        if (!distribution.viewInBrowserLinkText){
            distribution.viewInBrowserLinkText = this.localizationService
                .transform('Discover.distribution.otherSettings.viewInBrowser.linkText.default');
        }

        this.focusedDistribution = distribution;
        this.focusedDistribution$.next(this.focusedDistribution);

        this.sampleTestEmailTagValues = JSON.parse(JSON.stringify(this.SampleTestEmailTagDefaultValues));

        this.focusedDistribution.mergeTags.forEach(tag => {
            if (`[%${tag.key}%]` in this.sampleTestEmailTagValues && tag.fallback?.key) {
                this.sampleTestEmailTagValues[`[%${tag.key}%]`] = tag.fallback.key;
            }
        });

        this.distributionDirty = false;
        if (!distribution.id) {
            this.distributionDirty = true;
            this.setTextBlockToolbarFixedTop(128);
        } else {
            this.setTextBlockToolbarFixedTop(158);
        }
        this.distributionDirty$.next(this.distributionDirty);

        this.setValidationErrors$(new DistributionErrors());
        this.setFallbackValues(this.focusedDistribution.mergeTags);

        if (this.focusedDistribution.blocks) {
            this.setBlocks(this.focusedDistribution.blocks);
            this.setStylesOnBlocks(this.blocks);
            this.setTemplatesOnBlocks(this.blocks);
        }

        this.updateCachedDistribution();
        this.defaultFooterDirty$.next(false);

        if (fetchRecipientInfo) {
            this.fleshOutRecipientInformation();
        }

        this.blockMaxWidth$.next(this.focusedDistribution.generalStyles.distributionMaxWidthPx);

        this.updateBlocks();
    }

    createNewBlankDistribution(individualRecipientsListId?: number): void {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution = new DistributionModel();
        this.setFooterDefaultValues(newDistribution);

        newDistribution.generalStyles.setStyles(this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_1]);
        const firstTextBlock = new BlockModel(
            null,
            distributionTextBlockType,
            new RichTextDisplayElementModel(
                null,
                JSON.stringify(this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_1][DistributionBlockTypeEnum.text])
            )
        );
        firstTextBlock.displayElement.styles.isBoundToGlobalStyles = true;
        (firstTextBlock.displayElement as RichTextDisplayElementModel).enableAIHelper = true;
        (firstTextBlock.displayElement as RichTextDisplayElementModel).aiReferenceId = newDistribution.aiReferenceId;
        firstTextBlock.displayElement.isBoundToGlobalStyles = true;
        newDistribution.blocks = [firstTextBlock, ...newDistribution.blocks];

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    createPressReleaseDistribution(individualRecipientsListId?: number) {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution = DistributionFactoryModel.createPressReleaseDistribution(this.configurationService.isNewBranding());
        this.setFooterDefaultValues(
            newDistribution,
            this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_1][DistributionBlockTypeEnum.footer]
        );

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    createMinimalistDistribution(individualRecipientsListId?: number): void {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution = DistributionFactoryModel.createMinimalistDistribution();
        this.setFooterDefaultValues(
            newDistribution,
            this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_3][DistributionBlockTypeEnum.footer]
        );

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    createMarketingDistribution(individualRecipientsListId?: number): void {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution = DistributionFactoryModel.createMarketingDistribution(this.configurationService.isNewBranding());
        this.setFooterDefaultValues(
            newDistribution,
            this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_4][DistributionBlockTypeEnum.footer]
        );

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    createNewAiBlankDistribution(
        aiContent: AiDistributionContent,
        isPressRelease: boolean,
        individualRecipientsListId?: number,
        aiReferenceId?: string
    ): void {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution = DistributionFactoryModel.createAiBlankDistribution(
            this.configurationService.isNewBranding(),
            aiContent,
            isPressRelease,
            aiReferenceId
        );

        this.setFooterDefaultValues(newDistribution);
        this.setHeaderValues(newDistribution, aiContent.subjectLine, aiContent.previewText);

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    createActionAssistantDistribution(content: AiDistributionContent): void {
        this.setLoading(true);

        combineLatest([
            this.hasDefaultTemplates().pipe(filter(x => !!x), take(1)),
            this.stylesService.all$.pipe(filter(x => !!x), take(1)),
        ]).subscribe(() => {
            const newDistribution = new DistributionModel();
            this.setFooterDefaultValues(newDistribution);
            newDistribution.generalStyles.setStyles(this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_1]);

            const bodyBlock = new BlockModel(
                null,
                distributionTextBlockType,
                new RichTextDisplayElementModel(
                    content.body,
                    JSON.stringify(this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_1][DistributionBlockTypeEnum.text])
                )
            );
            bodyBlock.displayElement.styles.isBoundToGlobalStyles = true;
            (bodyBlock.displayElement as RichTextDisplayElementModel).enableAIHelper = true;
            (bodyBlock.displayElement as RichTextDisplayElementModel).aiReferenceId = newDistribution.aiReferenceId;
            bodyBlock.displayElement.isBoundToGlobalStyles = true;
            newDistribution.blocks = [bodyBlock, ...newDistribution.blocks];

            this.setHeaderValues(newDistribution, content.subjectLine, content.previewText);
            this.setCommonDistributionDetails(newDistribution);
            this.setDefaultSendFrom(newDistribution);
            this.setFocusedDistribution(newDistribution, false);
            this.cachedDistribution = null;
            this.distributionDirty = true;
            this.distributionDirty$.next(this.distributionDirty);
            this.setLoading(false);
        });
    }

    createAiPressReleaseDistribution(
        aiContent: AiDistributionContent,
        isPressRelease: boolean,
        individualRecipientsListId?: number,
        aiReferenceId?: string
    ) {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution =
            DistributionFactoryModel.createAiPressReleaseDistribution(this.configurationService.isNewBranding(), aiContent, isPressRelease, aiReferenceId);

        this.setFooterDefaultValues(newDistribution);
        this.setHeaderValues(newDistribution, aiContent.subjectLine, aiContent.previewText);

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    createAiMinimalistDistribution(
        aiContent: AiDistributionContent,
        isPressRelease: boolean,
        individualRecipientsListId?: number,
        aiReferenceId?: string
    ) {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution =
            DistributionFactoryModel.createAiMinimalistDistribution(this.configurationService.isNewBranding(), aiContent, isPressRelease, aiReferenceId);

        this.setFooterDefaultValues(newDistribution);
        this.setHeaderValues(newDistribution, aiContent.subjectLine, aiContent.previewText);

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    createAiMarketingDistribution(
        aiContent: AiDistributionContent,
        isPressRelease: boolean,
        individualRecipientsListId?: number,
        aiReferenceId?: string
    ) {
        this.discoverRoutingService.gotoDistribution();

        const newDistribution =
            DistributionFactoryModel.createAiMarketingDistribution(this.configurationService.isNewBranding(), aiContent, isPressRelease, aiReferenceId);

        this.setFooterDefaultValues(newDistribution, this.defaultStyles[DistributionStyleStateEnum.DISTRIBUTION_4][DistributionBlockTypeEnum.footer]);
        this.setHeaderValues(newDistribution, aiContent.subjectLine, aiContent.previewText);

        this.setCommonDistributionDetails(newDistribution, individualRecipientsListId);
        this.setDefaultSendFrom(newDistribution);
        this.setFocusedDistribution(newDistribution, false);
        this.cachedDistribution = null;

        // The individual recipient list id isn't part of the main service's list-of-lists
        // so we have to fetch it individually.  In other cases blank distros start with
        // no recipients.
        if (individualRecipientsListId) {
            this.fleshOutRecipientInformation();
        }

        this.distributionDirty = true;
        this.distributionDirty$.next(this.distributionDirty);
    }

    private setHeaderValues(distribution: DistributionModel, subjectLine: string, previewText: string): void {
        distribution.subjectLine = subjectLine;
        distribution.previewText = previewText;
    }

    private setDefaultSendFrom(distribution: DistributionModel): void{
        if (this.defaultSendFromConnector) {
            distribution.sendFrom = new DistributionSendFrom();
            distribution.sendFrom.connectionId = this.defaultSendFromConnector.id;
            distribution.sendFrom.connectionName = this.defaultSendFromConnector.connectorName;
            distribution.sendFrom.connectionEmail = this.defaultSendFromConnector.connectorEmail;
            distribution.sendFrom.grantId = this.defaultSendFromConnector.grant?.id;
        }
    }

    setFooterDefaultValues(distribution: DistributionModel, footerStyles?: any) {
        if (this.cachedFooter) {
            distribution.footer = this.cachedFooter.clone();
            distribution.setFooterBlock(distribution.footer, footerStyles);
            distribution.includeFooter = true;
        }

        if (!distribution.footer.senderEmail && !distribution.footer.senderPhone) {
            distribution.footer.senderEmail = this.currentUser.email;
        }
    }

    cloneFromExistingDistribution(id: number, individualRecipientsListId?: number): void {
        this.distributionApi.getById(id).subscribe(
            distributionApiModel => {
                distributionApiModel.aiReferenceId = null;
                this.focusedDistribution = new DistributionModel(distributionApiModel).clone();
                this.focusedDistribution.deliverySchedule = new DeliverySchedule(
                    this.focusedDistribution.deliverySchedule.scheduleType === ScheduleType.immediate || this.focusedDistribution.sentNow ? null
                        : this.focusedDistribution.deliverySchedule.scheduledDateUTC,
                    !!this.focusedDistribution.deliverySchedule.timeZone ? this.focusedDistribution.deliverySchedule.timeZone : null
                );
                delete this.focusedDistribution.id;
                this.focusedDistribution.sourceDistributionName = this.focusedDistribution.name + ' (copy)';
                this.focusedDistribution.name = null;
                if (distributionApiModel.type === DistributionType.TEMPLATE) {
                    delete this.focusedDistribution.name;
                    this.focusedDistribution.type = DistributionType.DISTRIBUTION;
                }
                this.focusedDistribution.status = DistributionStatus.draft;
                this.focusedDistribution.createdByUserId = null;
                this.focusedDistribution.createdDate = null;
                this.focusedDistribution.modifiedByUserId = null;
                this.focusedDistribution.modifiedDate = null;
                this.focusedDistribution.guid = null;
                this.focusedDistribution.bodyPlainText = null;
                this.focusedDistribution.footerPlainText = null;
                if (individualRecipientsListId) {
                    this.setCommonDistributionDetails(this.focusedDistribution, individualRecipientsListId, true);
                } else {
                    this.setCommonDistributionDetails(this.focusedDistribution, distributionApiModel.individualRecipientsListId, true);
                }
                this.setFocusedDistribution(this.focusedDistribution);
                this.cachedDistribution = null;
                this.distributionDirty = true;
                this.distributionDirty$.next(this.distributionDirty);
                this.discoverRoutingService.gotoDistribution();
            }
        );
    }

    public setStylesOnBlocks(blocks: BlockModel[]): void {
        if (!this.styles) {
            return;
        }
        for (const block of blocks) {
            if (block?.displayElement?.styles?.styleState) {
                // Set the template if needed
                if (
                    block.displayElement.styles.styleState !== StyleStateEnum.CUSTOM &&
                    block.displayElement.styles.styleState !== StyleStateEnum.DEFAULT
                ) {
                    const savedStyle = this.styles.find(style => style.id === block.displayElement.styles.styleState);
                    if (savedStyle && block.displayElement.styles.setFromApiModel) {
                        const style: GenericStyleModel = savedStyle.clone().style;
                        style.styleState = savedStyle.id;
                        if (savedStyle.template) {
                            style.template = savedStyle.template;
                        }
                        block.displayElement.styles.setFromApiModel(style);
                    }
                }
            }
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    this.setStylesOnBlocks(column);
                });
            }
        }
    }

    /**
     * Setter for blockToGeneralStylePropertiesMap
     */
    setBlockToGeneralStylePropertiesMap(blockGeneralStyles: Map<string, Map<string, string>>): void {
        this.blockToGeneralStylePropertiesMap = blockGeneralStyles;
    }

    updateBlocks() {
        if (!this.focusedDistribution) {
            return;
        }

        // if the selected block no longer exists, then deselect it
        if (!this.blockExists()) {
            // we don't need to cancel changes because it's not going to get saved anyway
            this.deselectBlock(true);
        }

        // Don't call this.setBlocks because it calls setCurrentDistributionBlocks and results in a infinite loop
        super.setBlocks(this.focusedDistribution.blocks);
        this.setGeneralStyles(this.focusedDistribution.generalStyles);
        this.recentColorsService.setContext(this.focusedDistribution.blocks, this.focusedDistribution.generalStyles);

    }
    /**
     * Handles changes to block content style change
     */
    updateContentBlockStyle(block: BlockModel, property: string, value: any): void {

        switch (value) {
            case DistributionStyleStateEnum.DEFAULT:
            case DistributionStyleStateEnum.DISTRIBUTION_1:
            case DistributionStyleStateEnum.DISTRIBUTION_2:
            case DistributionStyleStateEnum.DISTRIBUTION_3:
            case DistributionStyleStateEnum.DISTRIBUTION_4:
            case DistributionStyleStateEnum.DISTRIBUTION_5:
                const defaultStyle = this.getDefaultStyle(<DistributionBlockTypeEnum>block.type.key, value);
                this.setPropertyValue(block.displayElement, 'styles', defaultStyle.clone(block.displayElement.styles));
                break;
            case DistributionStyleStateEnum.CUSTOM:
                break;
            default:
                break;
        }

        if (!this.blockToGeneralStylePropertiesMap) {
            this.formFieldUpdated();

            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            this.setPropertyValue(this.generalStyles, <DistributionBlockTypeEnum>block.type.key,  block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, 'styles', block.displayElement.styles.clone());
        }

        block.displayElement.styles = block.displayElement.styles.clone();
        this.formFieldUpdated();
        this.selectBlock(block);
    }

    // TODO: Rework distribution save/update logic to use this code
    refetchDistribution(distributionId: number = this.focusedDistribution?.id): Observable<any> {
        this.setLoading(true);
        return this.distributionApi.getById(distributionId).pipe(
            map(distributionApiModel => {
                this.focusedDistribution = new DistributionModel(distributionApiModel);
                this.setLoading(false);
                const summaryReloadRequired = !this.focusedDistribution.summaryEquals(this.cachedDistribution);
                // Update the cached version
                this.setFocusedDistribution(this.focusedDistribution, true);
                if (summaryReloadRequired) {
                    this.reloadDistributionSummaries();
                }
            })
        );
    }

    private setCommonDistributionDetails(distribution: DistributionModel, individualRecipientsListId?: number, isCloned?: boolean) {
        if (isCloned) {
            if (individualRecipientsListId !== undefined) {
                distribution.individualRecipientsListId = individualRecipientsListId;
                distribution.recipientInformation.individualRecipientsListId = individualRecipientsListId;
            }
        } else {

            distribution.individualRecipientsListId = individualRecipientsListId;
            distribution.recipientInformation.individualRecipientsListId = individualRecipientsListId;
            distribution.fromName = this.userDistributionDetails.fromName;
            distribution.replyEmail = this.userDistributionDetails.replyTo.trim();
        }
        distribution.recipientInformation.addEmails(this.defaultRecipients ?? []);
    }

    /**
     * Determine which save case to use, and return a stream representing that workflow
     * @param presendSave whether this save is happening as a precursor to a send, in which case we can remove some duplicate logic
     * @returns a stream representing either the create or the save case for saving the distribution
     */
    private saveDistributionStream(presendSave: boolean): Observable<number> {
        if (this.focusedDistribution.id) { // Save case
            this.focusedDistribution.recipientInformation.recipientGroups.forEach(recipientGroup => {
                recipientGroup.recipients.forEach(recipient => {
                    if (recipient.status === DistributionRecipientStatus.deleted) {
                        recipientGroup.recipients.splice(recipientGroup.recipients.indexOf(recipient), 1);
                    }
                });
            });
            return this.distributionApi.save(this.focusedDistribution.toApiModel()).pipe(
                mergeMap((id) => {
                    return this.distributionApi.getById(id);
                }),
                map(distributionApiModel => {
                    this.focusedDistribution = new DistributionModel(distributionApiModel);
                    this.setEmailAccountConnectorName(this.emailAccountConnectors, this.focusedDistribution);
                    this.setLoading(false);

                    if (!presendSave) {
                        this.messageService.add({
                            severity : messageSeverityEnum.success,
                            detail: this.localizationService.transform('Discover.distribution.actions.saveDistribution', {
                                name: this.focusedDistribution.name
                            })
                        });
                    }

                    const summaryReloadRequired = !this.focusedDistribution.summaryEquals(this.cachedDistribution);

                    // Update the cached version
                    this.setFocusedDistribution(this.focusedDistribution, true);

                    if (summaryReloadRequired) {
                        this.reloadDistributionSummaries();
                    }

                    try {
                        this.pendoService.trackEvent({
                            type: 'DISTRIBUTION_SAVED',
                            data: {
                                distributionId: this.focusedDistribution.id
                            }
                        });
                    } catch (err) {
                        // Do nothing, this code should never stop following code from executing
                        console.error(err);
                    }

                    return this.focusedDistribution.id;
                })
            );
        } else { // Create case
            return this.distributionApi.save(this.focusedDistribution.toApiModel()).pipe(
                mergeMap((id) => {
                    return this.distributionApi.getById(id);
                }),
                map(distributionApiModel => {

                    this.focusedDistribution = new DistributionModel(distributionApiModel);
                    this.reloadDistributionSummaries();

                    try {
                        if (this.pendoCreateData) {
                            this.pendoCreateData.data.distributionId = this.focusedDistribution.id;
                            this.pendoCreateData.type = 'DISTRIBUTION_CREATED';
                            this.pendoService.trackEvent(this.pendoCreateData);
                        }
                    } catch (err) {
                        // Do nothing, this code should never stop following code from executing
                        console.error(err);
                    }

                    if (!presendSave) {
                        this.messageService.add({
                            severity : messageSeverityEnum.success,
                            detail: this.localizationService.transform('Discover.distribution.actions.saveDistribution', {
                                name: this.focusedDistribution.name
                            })
                        });

                        this.setLoading(false);
                        // Copy the current distribution to the cached distribution because they should now be in sync
                        this.updateCachedDistribution();

                        this.distributionDirty = false;
                        this.distributionDirty$.next(false);

                        this.discoverRoutingService.gotoDistribution(this.focusedDistribution.id);
                    } else {
                        this.setFocusedDistribution(this.focusedDistribution, true);
                    }
                    return this.focusedDistribution.id;
                })
            );
        }
    }

    // TODO: Rework distribution save/update logic to use this code
    saveDistributionWithoutRefetch(): Observable<number> {
        return this.distributionApi.save(this.focusedDistribution.toApiModel()).pipe(
            catchError((error: HttpErrorResponse) => {
                const errorModel = (error.error as CommonApiErrorModel);
                switch (errorModel?.details?.[0]?.code) {
                    case (CommonApiErrorEnum.duplicateName):
                        this.focusedDistribution.name = null;
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Discover.distribution.actions.errorHeading'),
                            detail: this.localizationService.transform('Discover.distribution.actions.errorMessageDuplicateName')
                        });
                        break;
                    case (CommonApiErrorEnum.invalidReplyToEmail):
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Discover.distribution.actions.errorHeading'),
                            detail: this.localizationService.transform('Discover.distribution.actions.invalidReplyEmail')
                        });
                        break;
                    default:
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Discover.distribution.actions.errorHeading'),
                            detail: this.localizationService.transform('Discover.distribution.actions.errorMessageGeneric')
                        });
                        break;
                }
                throw (error);
            })
        );
    }

    saveDistribution(presendSave: boolean = false): Observable<number> {
        if (!presendSave) {
            this.setLoading(true);
        }

        return this.saveDistributionStream(presendSave).pipe(
            catchError((error: HttpErrorResponse) => {
                const errorModel = (error.error as CommonApiErrorModel);
                switch (errorModel?.details?.[0]?.code) {
                    case (CommonApiErrorEnum.duplicateName):
                        this.focusedDistribution.name = null;
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Discover.distribution.actions.errorHeading'),
                            detail: this.localizationService.transform('Discover.distribution.actions.errorMessageDuplicateName')
                        });
                        break;
                    case (CommonApiErrorEnum.invalidReplyToEmail):
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Discover.distribution.actions.errorHeading'),
                            detail: this.localizationService.transform('Discover.distribution.actions.invalidReplyEmail')
                        });
                        break;
                    default:
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Discover.distribution.actions.errorHeading'),
                            detail: this.localizationService.transform('Discover.distribution.actions.errorMessageGeneric')
                        });
                        break;
                }
                if (!presendSave) {
                    this.setLoading(false);
                }
                throw (error);
            })
        );
    }

    async scheduleDistribution(scheduledTime: string): Promise<number> {
        let distributionId = this.focusedDistribution.id;
        let noSavedPlainText: boolean = false;
        if (!this.focusedDistribution.bodyPlainText) {
            noSavedPlainText = true;
            this.setPlainTextContent(this.focusedDistribution);
        }

        if (this.distributionDirty || noSavedPlainText) {
            try {
                distributionId = await lastValueFrom(this.saveDistribution(true));
            } catch (error) {
                return Promise.reject(error);
            }
        }

        const body = await this.getDistributionHtml();

        return new Promise<number> (
            (resolve, reject) => {
                this.distributionApi.scheduleDistribution(distributionId, body).subscribe({
                    next: () => {
                        if (this.focusedDistribution.deliverySchedule.scheduleType === ScheduleType.immediate) {
                            this.messageService.add({
                                severity : messageSeverityEnum.success,
                                summary: this.localizationService.transform('Discover.confirmation.confirmation.successMessage.immediate.summary'),
                                detail: this.localizationService.transform('Discover.confirmation.confirmation.successMessage.immediate.detail', {
                                    name: this.focusedDistribution.name
                                })
                            });
                        } else {
                            this.messageService.add({
                                severity : messageSeverityEnum.success,
                                summary: this.localizationService.transform('Discover.confirmation.confirmation.successMessage.scheduledDate.summary'),
                                detail: this.localizationService.transform('Discover.confirmation.confirmation.successMessage.scheduledDate.detail', {
                                    name: this.focusedDistribution.name,
                                    scheduledTime: scheduledTime
                                })
                            });
                        }
                        // Cheat the data, if we refreshed with GET this would likely be true:
                        this.focusedDistribution.status = DistributionStatus.scheduled;
                        this.focusedDistribution.id = distributionId;

                        if (this.distributionSummaries.find(x => x.id === distributionId)) {
                            this.distributionSummaries.find(x => x.id === distributionId).status = DistributionStatus.scheduled;
                            this.distributionSummaries$.next(this.distributionSummaries);
                        }

                        // Currently only nexting this to trigger an update of "schedulable flag"
                        // TODO : Revisit if this is inefficient
                        this.focusedDistribution$.next(this.focusedDistribution);
                        this.updateCachedDistribution();
                        this.updateDirtyFlag();

                        // Refetch prefs to force an update on the "available Distributions" field
                        this.targetingPreferencesService.reloadPreferences();

                        try {
                            this.pendoService.trackEvent({
                                type: 'DISTRIBUTION_SENT',
                                data: {
                                    distributionId: this.focusedDistribution.id,
                                    numberOfRecipients: this.focusedDistribution.recipientInformation.total
                                }
                            });
                        } catch (err) {
                            // This having an error should never block things
                            console.error(err);
                        }
                        resolve(distributionId);
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Discover.confirmation.confirmation.failureMessage'),
                        });
                        reject(error);
                    }
                });
            });
    }

    setPlainTextContent(distribution: DistributionModel) {
        distribution.bodyPlainText = this.plainTextService.generateBodyPlainText(distribution.blocks);
        distribution.footerPlainText = this.plainTextService.footerSeparator.repeat(40) + '\n\n' +
            this.plainTextService.generateFooterPlainText(distribution.blocks);
    }

    async sentTestDistribution(recipients: string[], setTestEmail?: boolean) {
        this.setLoading(true);

        // HACK for NEP-7773 -> "VIEW IN BROSWER" does not work with the "SEND TEST EMAIL" if the distribution is not saved first
        // we need to get the UUID of the distribution before sending the test email
        // this is because the post distribution only returns the ID, not the distribution object
        if (!this.focusedDistribution.id) {
            try {
                const distributionId = await lastValueFrom(this.saveDistribution(false));
                this.getDistributionById(distributionId).subscribe(distributionApiModel => {
                    this.focusedDistribution = new DistributionModel(distributionApiModel);
                    this.setFocusedDistribution(this.focusedDistribution, true);
                    this.sentTestDistribution(recipients, true);
                });
                return;
            } catch (error) {
                return Promise.reject(error);
            }
        }
        const allErrors = this.getValidationErrors(this.focusedDistribution, DistributionValidationLevel.SENDTESTEMAIL);
        if (allErrors.headerHasInvalidEmail) {
            this.setValidationErrors$(allErrors);
            this.setLoading(false);
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Discover.targeting.distribution.error.requiredInfo.summary'),
                detail: this.localizationService.transform('Discover.targeting.distribution.error.invalidReplyEmail')
            });
            return;
        } else if (allErrors.sendFromNeedsReAuth) {
            this.setValidationErrors$(allErrors);
            this.setLoading(false);
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Discover.targeting.distribution.error.reauth.summary'),
                detail: this.localizationService.transform('Discover.targeting.distribution.error.reauth.detail')
            });
            return;
        }

        const body = await this.getDistributionHtml({isTestEmail: true});
        if (this.focusedDistribution.bodyPlainText) {
            body.bodyPlainText = this.focusedDistribution.bodyPlainText + this.focusedDistribution.footerPlainText;
        } else {
            const distributionClone = this.focusedDistribution.clone();
            this.setPlainTextContent(distributionClone);
            body.bodyPlainText = distributionClone.bodyPlainText + distributionClone.footerPlainText;
        }

        const partialDistrib = this.focusedDistribution.toApiModel();

        // TODO : could localize these
        partialDistrib.subjectLine = !!partialDistrib.subjectLine ?
            partialDistrib.subjectLine : this.localizationService.transform('Discover.distribution.footer.test.subjectPlaceholder');
        partialDistrib.fromName = !!partialDistrib.fromName ?
            partialDistrib.fromName : this.localizationService.transform('Discover.distribution.footer.test.fromPlaceholder');
        partialDistrib.replyEmail = !!partialDistrib.replyEmail ? partialDistrib.replyEmail : 'no-reply@agilitypr.com';

        this.distributionApi.sendTestDistribution({
            body: body,
            distribution: partialDistrib,
            recipients: recipients
        }).pipe(
            finalize(() => this.setLoading(false))
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Discover.distribution.testEmail.successMessage.summary'),
                    detail: this.localizationService.transform('Discover.distribution.testEmail.successMessage.detail')
                });
                if (setTestEmail) {
                    this.setTestEmailSent(setTestEmail);
                }
            },
            error: (error) => {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: this.localizationService.transform('Discover.distribution.testEmail.errorMessage.summary'),
                    detail: this.localizationService.transform('Discover.distribution.testEmail.errorMessage.detail')
                });
            }
        });
    }

    setTestEmailSent(testEmailSent: boolean): void {
        this.testEmailSent = testEmailSent;
        this.testEmailSent$.next(this.testEmailSent);
    }

    getTestEmailSent(): Observable<boolean> {
        return this.testEmailSent$;
    }

    cancelDistribution(distribution: DistributionModel) {
        this.setLoading(true);

        // TODO : confirmation dialog
        this.distributionApi.cancelDistribution(distribution.id).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.distribution.actions.cancelDistribution.successSummary',
                    detail: this.localizationService.transform('Discover.distribution.actions.cancelDistribution.successDetail', {
                        name: distribution.name
                    })
                });

                this.setLoading(false);

                this.focusedDistribution.status = DistributionStatus.draft;
                this.reloadDistributionSummaries();

                // Currently only nexting this to trigger an update of "schedulable flag"
                // TODO : Revisit if this is inefficient
                this.focusedDistribution$.next(this.focusedDistribution);
                this.discoverRoutingService.gotoDistribution(this.focusedDistribution.id);
            },
            error: () => {
                const confirmDialogModel: ConfirmDialogModel = {
                    inputs: {
                        heading: 'Discover.distribution.actions.cancelDistribution.failureHeading',
                        showCancel: false,
                        message: this.localizationService.transform('Discover.distribution.actions.cancelDistribution.failureMessage', {
                            name: distribution.name
                        }),
                        okLabel: 'Discover.distribution.actions.cancelDistribution.failureAcknowledge',
                        okLabelLeft: true
                    },
                    outputs: {
                        dialogClose: async (response) => {
                            this.setLoading(false);
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
            }
        });
    }

    updateDistributionVisibility(id: number, action: ActionsEnum){
        const hidden = action === ActionsEnum.hide;
        this.distributionApi.getById(id).pipe(
            switchMap( _distribution => {
                _distribution.hidden = hidden;
                if (_distribution.status !== DistributionStatus.sent){
                    return this.distributionApi.update(_distribution);
                } else {
                    const summary: DistributionSummaryApiModel = {
                        id: _distribution.id,
                        hidden: _distribution.hidden
                    };
                    return this.distributionApi.updateSentDistribution(summary);
                }
            })
        ).subscribe(() => {
            this.reloadDistributionSummaries();
        });
    }

    public bulkUpdateVisibility(ids: number[], action: ActionsEnum) {
        const hidden = action === ActionsEnum.hide;
        const observables = ids.map(id => {
            return this.distributionApi.getById(id).pipe(
                switchMap(distribution => {
                    if (distribution.status !== DistributionStatus.sent) {
                        distribution.hidden = hidden;
                        return this.distributionApi.update(distribution);
                    } else {
                        const summary: DistributionSummaryApiModel = {
                            id: distribution.id,
                            hidden: hidden
                        };
                        return this.distributionApi.updateSentDistribution(summary);
                    }
                })
            );
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.bulkUpdateDistribution.success.summary',
                    detail: this.localizationService.transform('Discover.bulkUpdateDistribution.success.detail', {number: result.length})
                });
                this.reloadDistributionSummaries();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.bulkUpdateDistribution.error.summary',
                    detail: 'Discover.bulkUpdateDistribution.error.detail'
                });
                this.reloadDistributionSummaries();
            }
        });
    }

    /**
     * Update from LHP with text input dialog
     * @param id
     * @param initialName
     */
    renameDistribution(id: number, initialName?: string){
        const inputConfig: TextInputDialogModel = {
            inputs: {
                width: 280,
                heading: 'Discover.renameDistritbuion',
                okLabel: 'Lhp.actions.rename',
                placeholder: 'Discover.distribution.rename.placeholder',
                description: 'Discover.distribution.rename.name',
                initialValue: initialName,
                formValidator: (name: string) => {
                    if (this.distributionExistsWithName(id, name)) {
                        return [
                            'Discover.distribution.actions.errorMessageDuplicateName'
                        ];
                    } else {
                        return [];
                    }
                },
                submit: (name: string) => {
                    // The scenario that user happens to edit the focused distribution
                    if (this.focusedDistribution && id === this.focusedDistribution.id) {
                        this.focusedDistribution.name = name;
                        this.cachedDistribution.name = name;
                    }

                    this.updateDistributionName(id, name);

                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, inputConfig);
    }

    /**
     * Update from LHP Management Dialog table
     * @param id
     * @param name
     */
    updateDistributionName(id: number, name: string) {
        this.distributionApi.getById(id).pipe(
            mergeMap(_dist => {
                _dist.name = name;
                return this.distributionApi.update(_dist);
            })
        ).subscribe({
            next: () =>{
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.distribution.renameDistributionSuccessHeading',
                    detail: this.localizationService.transform('Discover.distribution.renameDistributionSuccessMessage', {name: name})
                });
                this.reloadDistributionSummaries();
            },
            error: (err) => {
                if (err.error?.details?.[0]?.code === CommonApiErrorEnum.duplicateName) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.distribution.renameDistributionErrorHeading',
                        detail: 'Discover.distribution.actions.errorMessageDuplicateName'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.distribution.renameDistributionErrorHeading',
                        detail: 'Discover.distribution.renameDistributionErrorMessage'
                    });
                }
                this.reloadDistributionSummaries();
            }
        });
    }

    deleteDistribution(id: number, label: string, status: DistributionStatus) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'discover.deleteListConfirmDialog',
                heading: this.localizationService.transform('Discover.confirmDialog.deleteDistribution.heading'),
                message: this.localizationService.transform('Discover.confirmDialog.deleteDistribution.message'),
                alertMessage: status === DistributionStatus.scheduled ?
                    this.localizationService.transform('Discover.confirmDialog.deleteDistribution.alert')
                    : status === DistributionStatus.sent ? this.localizationService.transform('Discover.confirmDialog.deleteSentDistribution.alert')
                        : null,
                alertType: messageSeverityEnum.warning,
                list: [label],
                okLabel: this.localizationService.transform('Tags.manageDialog.delete')
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.setLoading(true);

                        this.distributionApi.deleteById(id).subscribe(x => {
                            this.messageService.add({
                                severity : messageSeverityEnum.success,
                                detail: 'Discover.confirmDialog.deleteDistribution.success'
                            });
                            this.setLoading(false);

                            if (id === this.focusedDistribution?.id) {
                                this.discoverRoutingService.gotoDistributionHype();
                            }
                            this.reloadDistributionSummaries();
                        });
                    } else {
                        this.reloadDistributionSummaries();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    public confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const names = [];
        const ids = [];
        const scheduledDistributions = [];
        const otherDistributions = [];
        (items as StatusLhpItem[]).forEach(item => {
            if (item.status === DistributionStatus.scheduled) {
                scheduledDistributions.push(item);
            } else {
                otherDistributions.push(item);
            }
        });

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'bulkDeleteScheduledDistribution',
                    heading: 'Discover.confirmDialog.bulkDeleteDistribution.heading',
                    message: 'Common.confirmDialog.message',
                    list: names,
                    listLabel: 'Discover.confirmDialog.bulkDeleteDistribution.checkboxLabel',
                    isListSelected,
                    secondaryList: groups.map(item => item.name),
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                this.bulkDeleteDistributions(ids);
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };

            if (scheduledDistributions.length) {
                scheduledDistributions.forEach(item => {
                    ids.push(item.id);
                    names.push(item.name);
                });

                const confirmScheduledConfig: ConfirmDialogModel = {
                    inputs: {
                        id: 'bulkDeleteScheduledDistribution',
                        heading: 'Discover.confirmDialog.bulkDeleteDistribution.heading',
                        alertMessage: 'Discover.confirmDialog.bulkDeleteDistribution.alert',
                        alertType: messageSeverityEnum.warning,
                        list: names,
                        bottomMessage: 'Discover.confirmDialog.bulkDeleteDistribution.alert.message',
                        okLabel: 'continue'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                otherDistributions.forEach(item => {
                                    names.push(item.name);
                                    ids.push(item.id);
                                });
                                this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
                            } else {
                                subscriber.next(false);
                                subscriber.complete();
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmScheduledConfig);
            } else {
                otherDistributions.forEach(item => {
                    names.push(item.name);
                    ids.push(item.id);
                });
                this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
            }
        });
    }

    private bulkDeleteDistributions(ids: number[]) {
        const observables = ids.map(id => {
            return this.distributionApi.deleteById(id);
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.confirmDialog.bulkDeleteDistribution.success.summary',
                    detail: this.localizationService.transform('Discover.confirmDialog.bulkDeleteDistribution.success.detail', {number: result.length})
                });
                if (this.focusedDistribution && ids.includes(this.focusedDistribution.id)) {
                    this.discoverRoutingService.gotoDistributionHype();
                }
                this.reloadDistributionSummaries();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.confirmDialog.bulkDeleteDistribution.error.summary',
                    detail: 'Discover.confirmDialog.bulkDeleteDistribution.error.detail'
                });
                this.reloadDistributionSummaries();
            }
        });
    }

    /**
     * Adds provided distributions to a group (or removes group ID if Uncategorized is selected)
     * @param distributionIds
     * @param groupId Group ID or undefined if Uncategorized is selected
     */
    public updateGroup(distributionIds: number[], group: DisplayGroupModel) {
        let request;
        if (group.id) {
            request = this.distributionApi.updateDistributionDisplayGroup(distributionIds, [], group.id);
        } else {
            request = this.distributionApi.removeDistributionsFromDisplayGroup(distributionIds);
        }

        request.subscribe({
            next: () => {
                this.reloadDistributionSummaries();
                if (distributionIds.length === 1) {
                    const itemName = this.distributionSummaries.find(dist => dist.id === distributionIds[0]).name;
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.single',
                            {itemName: itemName, groupName: group.name}
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.multiple',
                            {number: distributionIds.length, name: group.name}
                        )
                    });
                }
            },
            error: (err) => {
                this.reloadDistributionSummaries();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Removes group ID from all provided searches
     * @param distributionIds topic IDs
     */
    public removeAllDistributionsFromGroup(distributionIds: number[]) {
        this.distributionApi.removeDistributionsFromDisplayGroup(distributionIds).subscribe({
            next: () => {
                this.reloadDistributionSummaries();
            }
        });
    }

    public updateFavorites(addIds: number[], removeIds: number[]) {
        this.distributionApi.updateDistributionFavorites(addIds, removeIds).subscribe({
            next: () => {
                this.reloadDistributionSummaries();
            },
            error: (err) => {
                this.reloadDistributionSummaries();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    reloadDistributionSummaries(): Subscription {
        return this.distributionApi.getList().subscribe(
            summaries => {
                this.distributionSummaries = summaries.map(summary => new DistributionSummaryModel(summary));
                this.distributionSummaries$.next(this.distributionSummaries);
                this.targetingSearchService.addDistributionsToFilters(this.distributionSummaries);
            }
        );
    }

    getDistributionSummaries(): Observable<DistributionSummaryModel[]> {
        return this.distributionSummaries$;
    }

    getFilterListItems(): Observable<AuditableModel[]> {
        return this.getDistributionSummaries();
    }

    getFocusedResource(): Observable<DistributionModel> {
        return this.focusedDistribution$;
    }

    getDefaultFooterDirty(): Observable<boolean> {
        return this.defaultFooterDirty$.pipe(
            distinctUntilChanged()
        );
    }

    getValidationErrors(distribution: DistributionModel, validationLevel: DistributionValidationLevel) {
        const errors = new DistributionErrors();
        if (!this.focusedDistribution) {
            return errors;
        }
        errors.hasNoName = !this.focusedDistribution.name || !(this.focusedDistribution.name.trim().length > 0);
        errors.nameExists = this.distributionExistsWithName(this.focusedDistribution.id, this.focusedDistribution.name);
        errors.emptyBlocks = this.checkForEmptyBlocks(this.focusedDistribution.blocks);
        errors.invalidBlocks = this.checkForInvalidBlocks(this.focusedDistribution.blocks);

        if (validationLevel === DistributionValidationLevel.SEND) {
            errors.hasNoRecipients = !(distribution?.recipientInformation?.total > 0);
            errors.exceedsQuota = this.distributionQuotaInfo?.totalDistributionsAvailable < distribution?.recipientInformation?.total;
            errors.headerHasNoSubjectLine = !(distribution.subjectLine && distribution.subjectLine.trim().length > 0);
            errors.headerHasNoFrom = !(distribution.fromName && distribution.fromName.trim().length > 0);
            errors.headerHasNoReplyTo = !(distribution.replyEmail && distribution.replyEmail.trim().length > 0);
            errors.headerHasInvalidEmail = !errors.headerHasNoReplyTo && !validateEmail(distribution.replyEmail);
            errors.sendFromNeedsReAuth = !!(distribution.sendFrom?.requiresReAuthentication);

            if (this.focusedDistribution.includeFooter) {
                errors.footerHasNoCompany = !(distribution.footer.company?.trim().length > 0);
                errors.footerHasNoStreetAddress = !(this.focusedDistribution.footer.streetAddress?.trim().length > 0);
                errors.footerHasNoCity = !(this.focusedDistribution.footer.city?.trim().length > 0);
                errors.footerHasNoCountry = !(this.focusedDistribution.footer.country?.trim().length > 0);
                errors.footerHasNoZIP = !(this.focusedDistribution.footer.postalCode?.trim().length > 0);
                errors.footerHasNoContact =
                    !(this.focusedDistribution.footer.senderEmail?.trim().length > 0)
                    && !(this.focusedDistribution.footer.senderPhone?.trim().length > 0);
                errors.footerHasInvalidEmail = this.focusedDistribution.footer.senderEmail?.trim().length > 0
                    && !validateEmail(this.focusedDistribution.footer.senderEmail);

                this.footerMergeTags.forEach(tag => {
                    if (!this.focusedDistribution.footer.text.includes(tag)) {
                        errors.missingTags.push(tag);
                    }
                });
                errors.footerMissingMergeTags = errors.missingTags.length > 0;
            }

            errors.deliveryScheduleInvalid = !this.focusedDistribution.deliverySchedule.isValid();

        } else if (validationLevel === DistributionValidationLevel.SENDTESTEMAIL) {
            errors.headerHasInvalidEmail = distribution.replyEmail && distribution.replyEmail.trim().length > 0 && !validateEmail(distribution.replyEmail);
            errors.sendFromNeedsReAuth = (distribution.sendFrom?.requiresReAuthentication);
        }

        return errors;
    }

    setValidationErrors$(errors: DistributionErrors) {
        this.validationErrors$.next(errors);
    }

    getValidationErrors$(): Observable<DistributionErrors> {
        return this.validationErrors$;
    }

    /**
     * Checks for invalid blocks and updates inline feedback
     * @param blocks Blocks to be checked
     * @returns true if any errors present
     */
    private checkForInvalidBlocks(blocks: BlockModel[]): boolean {
        let blockErrors = false;
        for (let i = 0; i < blocks.length; i ++) {
            const block = blocks[i];
            let error = null;
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        error = this.checkForInvalidBlock(x);
                        if (!!error) {
                            x.errors.push(error);
                            blockErrors = true;
                        }
                    });
                });
            } else {
                error = this.checkForInvalidBlock(block);
                if (!!error) {
                    block.errors.push(error);
                    blockErrors = true;
                }
            }
        }
        return blockErrors;
    }

    checkForInvalidBlock(block: BlockModel): string {
        let error;
        switch (block.type.key) {
            case DistributionBlockTypeEnum.image: {
                const displayElement = block.displayElement as ImageDisplayElementModel;
                if (displayElement.image?.url?.length > 1000) {
                    error = this.localizationService.transform('Discover.targeting.distribution.error.imageMaxURL');
                }
                break;
            };
            default:
                break;
        }
        return error;
    }

    /**
     * Checks blocks for emptiness and updates inline feedback
     * @param blocks Blocks to be checked
     * @returns true if any errors present
     */
    private checkForEmptyBlocks(blocks: BlockModel[]): boolean {
        let blockErrors = false;
        for (let i = 0; i < blocks.length; i ++) {
            const block = blocks[i];
            let error = null;
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        x.errors = [];
                        error = this.checkForEmptyBlock(x);
                        if (!!error) {
                            x.errors.push(error);
                            blockErrors = true;
                        }
                    });
                });
            } else {
                block.errors = [];
                error = this.checkForEmptyBlock(block);
                if (!!error) {
                    block.errors.push(error);
                    blockErrors = true;
                }
            }
        }
        return blockErrors;
    }

    checkForEmptyBlock(block: BlockModel): string {
        let error;
        switch (block.type.key) {
            case DistributionBlockTypeEnum.title:
            case DistributionBlockTypeEnum.text:
                error = this.checkForEmptyTextBlock(block);
                break;
            case DistributionBlockTypeEnum.image: {
                error = this.checkForEmptyImageBlock(block);
                break;
            }
            case DistributionBlockTypeEnum.button:
                error = this.checkForEmptyButtonBlock(block);
                break;
            case DistributionBlockTypeEnum.link:
                error = this.checkForEmptyLinkBlock(block);
                break;
            default:
                break;
        }
        return error;
    }

    checkForEmptyTextBlock(block: BlockModel): string {
        const displayElement = block.displayElement as RichTextDisplayElementModel;
        let error;
        if (!displayElement.text || !displayElement.text.trim()) {
            if (block.type.key === DistributionBlockTypeEnum.title) {
                error = this.localizationService.transform('Discover.targeting.distribution.error.titleRequired');
            } else {
                error = this.localizationService.transform('Discover.targeting.distribution.error.textRequired');
            }
        }
        return error;
    }

    checkForEmptyImageBlock(block: BlockModel): string {
        const displayElement = block.displayElement as ImageDisplayElementModel;
        let error;
        if (!displayElement.image?.url || !displayElement.image?.url.trim()) {
            error = this.localizationService.transform('Discover.targeting.distribution.error.imageRequired');
        }
        return error;
    }

    checkForEmptyButtonBlock(block: BlockModel): string {
        const displayElement = block.displayElement as ButtonDisplayElementModel;
        let error;
        if (!displayElement.text || !displayElement.text.trim()) {
            error = this.localizationService.transform('Discover.targeting.distribution.error.buttonTextRequired');
        }
        return error;
    }

    checkForEmptyLinkBlock(block: BlockModel): string {
        const displayElement = block.displayElement as LinkDisplayElementModel;
        let error;
        if (!displayElement.links || !displayElement.links.length) {
            error = this.localizationService.transform('Discover.targeting.distribution.error.linkRequired');
        } else if (displayElement.links.some(link => !link.url)) {
            error = this.localizationService.transform('Discover.targeting.distribution.error.linkUrlRequired');
        }
        return error;
    }

    updateDirtyFlag() {
        // sent distribution is always clean because we can not modify it
        if (this.focusedDistribution.status === DistributionStatus.sent){
            this.distributionDirty$.next(false);
            this.defaultFooterDirty$.next(false);
            return;
        }

        const distributionDirty = !this.focusedDistribution.equals(this.cachedDistribution);
        this.distributionDirty = distributionDirty;
        this.distributionDirty$.next(distributionDirty);

        let footerDirty = false;
        if (!this.focusedDistribution.footer && this.cachedFooter) {
            footerDirty = true;
        } else {
            footerDirty = this.cachedFooter && !this.focusedDistribution.footer?.equals(this.cachedFooter);
        }

        this.defaultFooterDirty$.next(footerDirty);
    }

    restoreCachedDistribution() {
        this.showExitDialog().subscribe(doIt => {
            if (doIt) {
                this.setFocusedDistribution(this.cachedDistribution);
            }
        });
    }

    fleshOutRecipientInformation() {
        if (this.focusedDistribution) {
            const listRecipients = this.focusedDistribution.recipientInformation.getGroupRecipientsByRecipientType(RecipientType.list);

            if (this.listSummaries && listRecipients?.length) {
                for (const listRecipient of listRecipients) {
                    const listSummary = this.listSummaries.find(x => x.id === listRecipient.targetingListId);
                    if (!listSummary) {
                        listRecipient.status = DistributionRecipientStatus.deleted;
                    } else {
                        this.focusedDistribution.recipientInformation.addList(listSummary);
                    }
                }
                // update recipient count based on excluded contacts
                this.focusedDistribution.updateRecipientGroupCount(RecipientType.list);
            }

            const portfolioRecipients = this.focusedDistribution.recipientInformation.getGroupRecipientsByRecipientType(RecipientType.portfolio);

            if (this.portfolios && portfolioRecipients?.length) {
                for (const portfolioRecipient of portfolioRecipients) {
                    const portfolio = this.portfolios.find(x => x.targetingListId === portfolioRecipient.portfolioId);
                    if (!portfolio) {
                        console.log('Distrib is referencing a deleted portfolio or something');
                        portfolioRecipient.status = DistributionRecipientStatus.deleted;
                    } else {
                        this.focusedDistribution.recipientInformation.addPortfolio(portfolio);
                    }
                }
            }

            const searchRecipients = this.focusedDistribution.recipientInformation.getGroupRecipientsByRecipientType(RecipientType.search);

            if (this.searchSummaries && searchRecipients?.length) {
                for (const searchRecipient of searchRecipients) {
                    const searchSummary = this.searchSummaries.find(x => x.id === searchRecipient.targetingSearchId);
                    if (!searchSummary) {
                        console.log('Distrib is referencing a deleted search or something');
                        searchRecipient.status = DistributionRecipientStatus.deleted;
                    } else {
                        this.focusedDistribution.recipientInformation.addSearch(searchSummary);
                    }
                }
            }

            // Unsub so that if the user switches to another distrib before these calls complete,
            // there will not be any adverse effects.
            if (this.recipientCountSubscription) {
                this.recipientCountSubscription.unsubscribe();
            }

            const searchCountCalls: Observable<any>[] = [];
            for (const searchRecipient of searchRecipients) {
                if (searchRecipient.targetingListId && searchRecipient.targetingSearchId) {
                    searchCountCalls.push(
                        this.targetingListService.getListCount(searchRecipient.targetingListId).pipe(
                            tap(count => {
                                this.focusedDistribution.recipientInformation.addSearch({
                                    id: searchRecipient.targetingSearchId,
                                    count: count,
                                    editDisabled: true
                                });
                            })
                        )
                    );
                } else {
                    searchCountCalls.push(
                        this.targetingSearchesService.getSearchCount(searchRecipient.targetingSearchId, searchRecipient.filterSearch).pipe(tap(count => {
                            this.focusedDistribution.recipientInformation.addSearch({
                                id: searchRecipient.targetingSearchId,
                                count: count
                            });
                        }))
                    );
                }
            }

            this.recipientCountSubscription = forkJoin(searchCountCalls).subscribe(
                () => {
                    // update recipient count based on excluded contacts
                    this.focusedDistribution.updateRecipientGroupCount(RecipientType.search);
                    this.focusedDistribution$.next(this.focusedDistribution);
                }
            );

            if (this.focusedDistribution.individualRecipientsListId) {
                this.targetingListsApiService.getById(this.focusedDistribution.individualRecipientsListId, true).subscribe(list => {
                    const summary = new TargetingListList();
                    summary.id = list.id;
                    summary.name = this.localizationService.transform('Discover.recipientEditor.labels.individualRecipients');
                    summary.count = list.count;
                    summary.contactCount = list.contactCount;
                    summary.outletCount = list.outletCount;
                    this.focusedDistribution.recipientInformation.addList(summary);

                    // update recipient count based on excluded contacts
                    this.focusedDistribution.updateRecipientGroupCount(RecipientType.individual);
                    this.focusedDistribution$.next(this.focusedDistribution);
                });
            }

            if (this.focusedDistribution.recommendedRecipientsListId) {
                this.targetingListsApiService.getById(this.focusedDistribution.recommendedRecipientsListId, true).subscribe(list => {
                    const summary = new TargetingListList();
                    summary.id = list.id;
                    summary.name = this.localizationService.transform('Discover.recipientEditor.labels.prCoPilotRecipients');
                    summary.count = list.count;
                    this.focusedDistribution.recipientInformation.addList(summary);

                    // update recipient count based on excluded contacts
                    this.focusedDistribution.updateRecipientGroupCount(RecipientType.recommended);
                    this.focusedDistribution$.next(this.focusedDistribution);
                });
            }

            this.focusedDistribution$.next(this.focusedDistribution);
        }
    }

    updateRecipients(recipientInformation: RecipientInformationModel, excludedRecipients: ExcludedRecipientApiModel[]) {
        this.focusedDistribution.recipientInformation = recipientInformation;
        // if users delete individual contacts/outlets, we've reset individualRecipientsListId to null in RecipientInformationModel
        this.focusedDistribution.individualRecipientsListId = recipientInformation.individualRecipientsListId;
        this.focusedDistribution.recommendedRecipientsListId = recipientInformation.recommendedRecipientsListId;
        this.focusedDistribution.excludedRecipients = excludedRecipients;

        // Currently only nexting this to trigger an update of "schedulable flag"
        // TODO : Revisit if this is inefficient
        this.focusedDistribution$.next(this.focusedDistribution);
        this.updateDirtyFlag();
    }

    getDirtyFlag(): Observable<boolean> {
        return this.distributionDirty$.pipe(
            distinctUntilChanged()
        );
    }

    showExitDialog(): Observable<boolean> {
        if (!this.distributionDirty) {
            return of(true);
        }

        return new Observable<boolean>(
            (observer: Observer<boolean>) => {
                const confirmConfig: ConfirmDialogModel = {
                    inputs: {
                        id: 'Distribution.showExitDialog',
                        heading: this.localizationService.transform('DeactivateGuard.heading'),
                        messageHtml: this.localizationService.transform('DeactivateGuard.message'),
                        okLabel: this.localizationService.transform('Discover.distribution.confirm')
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed){
                                this.distributionDirty = false;
                                this.distributionDirty$.next(this.distributionDirty);
                            }
                            observer.next(confirmed);
                            observer.complete();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
            }
        );
    }

    setLoading(loading: boolean): void {
        this.loading = loading;
        this.loading$.next(this.loading);
    }

    getLoading(): Observable<boolean> {
        return this.loading$;
    }

    distributionExistsWithName(id: number, distributionName: string): boolean {
        return (distributionName
            && this.distributionSummaries?.some(x => x.name.toLocaleLowerCase() === distributionName.toLocaleLowerCase() && x.id !== id));
    }

    /**
     * Sets the default template on the distribution block types
     */
    setDefaultTemplatesOnBlockTypes(blockTypes: BlockTypeModel[]): BlockTypeModel[] {
        return blockTypes.map(blockType => {
            blockType.twig = this.getDefaultTemplateByBlockType(
                blockType.key,
                DistributionStyleStateEnum.DISTRIBUTION_1
            );
            return blockType;
        });
    }

    setBlocks(blocks: BlockModel[]) {
        super.setBlocks(blocks);
        this.focusedDistribution.blocks = blocks;
        this.updateDirtyFlag();
    }

    setFooter(footer) {
        this.focusedDistribution.footer = new FooterModel(footer);
        this.updateDirtyFlag();
    }

    // Gets localized templates where applicable
    cacheTemplates(templateList: Template[]) {
        templateList.forEach(template => {
            if (template.isDefault && template.defaultReference) {
                let templateString;
                if (template.template) {
                    templateString = template.template;
                } else if (template.templates) {
                    const language = this.localizationService?.uiLanguageTag?.substring(0, 2);
                    if (template.templates[language]) {
                        templateString = template.templates[language];
                    }
                }
                this.defaultTemplates.set(
                    template.type.toUpperCase() + (template.defaultReference?.toUpperCase() || ''),
                    templateString
                );
            }
        });

        this.hasDefaultTemplates$.next(true);
    }

    async getDistributionHtml(renderOptions: DistributionRenderOptions = {}): Promise<ResourceBodyApiModel> {
        const blocks: BlockModel[] = [];
        for (let i = 0; i < this.blocks.length; i++) {
            blocks[i] = this.blocks[i].clone();
        }
        let html: string = '';
        for (const block of blocks) {
            switch (block.type.key) {
                case DistributionBlockTypeEnum.footer:
                    // presend render is used to send different values to the backend from what is needed to render in the frontend.
                    // Currently, it's for adjusting footer's values so that optional fields will display the placeholder in the central panel,
                    // but not display anything in the device preview and in the actual email if the field is empty
                    block.renderedOutput = this.presendRenderFooter(block, !!renderOptions.isTestEmail);
                    break;
                case DistributionBlockTypeEnum.oneColumnLayout:
                case DistributionBlockTypeEnum.twoColumnLayout:
                case DistributionBlockTypeEnum.threeColumnLayout:
                    const twigData = Object.assign({}, block.displayElement, block.displayElement.settings);
                    twigData.html = [];
                    this.adjustLayoutBlockColumnWidth(twigData);
                    const layoutBlockBlocks = (block as LayoutBlock).blocks;
                    for (let columnIndex = 0; columnIndex < layoutBlockBlocks.length; columnIndex++) {
                        const column = layoutBlockBlocks[columnIndex];
                        const thisColumnHtml = [];
                        for (let positionInColumn = 0; positionInColumn < column.length; positionInColumn++) {
                            const x = column[positionInColumn];
                            thisColumnHtml.push(await this.generateBlockOutput(x, parseInt(twigData.styles.width[columnIndex], 10)));
                        }
                        twigData.html.push(thisColumnHtml.join(''));
                    }
                    twigData.blocks = (block as LayoutBlock).blocks;
                    const renderedOutput = this.twigRenderService.renderTemplate(block.type.twig, twigData);
                    block.renderedOutput = renderedOutput;
                    break;
                default:
                    block.renderedOutput = await this.generateBlockOutput(block);

            }

            if (block.renderedOutput) {
                html += block.renderedOutput;
            }
        }

        let viewInBrowserUrl = null;
        const viewInBrowserText = this.focusedDistribution.viewInBrowserLinkText;

        if (this.focusedDistribution.includeViewInBrowserLink && this.focusedDistribution.guid && !renderOptions.isTestEmail) {
            viewInBrowserUrl = this.configurationService.getConfig().apiUrl.split('/api/v1')[0] +
                '/distributions/history/' + this.focusedDistribution.guid;
        } else if (this.focusedDistribution.includeViewInBrowserLink && !!renderOptions.isTestEmail) {
            viewInBrowserUrl = this.configurationService.getConfig().apiUrl.split('/api/v1')[0] +
                '/distributions/history/preview';
        }

        // TODO: Use this logic in validation of distribution instead of de-duping after the fact
        const mergeTags: MergeTagApiModel[] = [];


        let viewInBrowserContainerUrl = null;
        if (this.focusedDistribution.includeViewInBrowserLink) {
            viewInBrowserContainerUrl = '[%VIEW_IN_BROWSER_URL%]';
        }

        let viewInBrowserUrlToUse = viewInBrowserContainerUrl;
        if (!!renderOptions.hideViewInBrowserUrl) {
            viewInBrowserUrlToUse = null;
        } else if (!!renderOptions.disableViewInBrowserLink) {
            viewInBrowserUrlToUse = 'javascript:void(0);';
        }
        const containerData = {
            html: html,
            preheaderText: this.processMergeTags(this.focusedDistribution.previewText, mergeTags),
            viewInBrowserUrl: viewInBrowserUrlToUse,
            viewInBrowserText: !!renderOptions.hideViewInBrowserUrl ? null : viewInBrowserText,
            maxWidthPx: this.focusedDistribution.generalStyles.distributionMaxWidthPx || 1200
        };
        const template = Twig.twig({
            // TODO: use distribution container when available
            data: this.defaultTemplates.get('CONTAINERDISTRIBUTION_CONTAINER')
        });
        let output = template.render({data: containerData});

        output = EmailHelper.convertFontSizeUnitsToEM(output, 14);

        output = this.processMergeTags(output, mergeTags);
        const subject = this.processMergeTags(this.focusedDistribution.subjectLine, mergeTags);

        const dedupedMergeTags = new Map<string, MergeTagApiModel>();
        for (const tag of mergeTags) {
            if (!dedupedMergeTags.has(tag.key)) {
                dedupedMergeTags.set(tag.key, tag);
                continue;
            }

            // Keep the first fallback value we find
            const existingTag = dedupedMergeTags.get(tag.key);
            if (existingTag.fallback) {
                continue;
            }
            if (tag.fallback) {
                dedupedMergeTags.set(tag.key, tag);
            }
        }

        // Replacing sample tag values for test/preview
        if (!!renderOptions.isTestEmail) {
            output = this.renderFallbackTagsToDefault(output);
        }

        return {
            bodyHtml: output,
            mergeTags: [...dedupedMergeTags.values()],
            subject: subject,
            viewInBrowserUrl: renderOptions.hideViewInBrowserUrl ? null : viewInBrowserUrl,
        };
    }

    async generateBlockOutput(block: BlockModel, width?: number): Promise<string> {
        // we have to re render the blocks because when you save the distribution before getDistributionHtml() it "resets" renderedOutput to null
        const data: any = {};
        const twigData = Object.assign(data, block.displayElement, block.displayElement.settings);
        if (twigData?.image?.url) {
            const { url, altText } = twigData.image;
            twigData.url = url;
            twigData.altText = altText;
        }

        twigData.styles.maxWidthPx = width || this.focusedDistribution.generalStyles.distributionMaxWidthPx || 1200;

        if (block.type.key === DistributionBlockTypeEnum.text && !(<RichTextDisplayElementModel>block.displayElement).htmlEdit) {
            twigData.text = await EmailHelper.setInlineImageWidth(twigData.text, twigData.styles.maxWidthPx);
            twigData.text = EmailHelper.updateLineHeightForEmail(twigData.text);
        }

        return this.twigRenderService.renderTemplate(block.type.twig, twigData);
    }

    adjustLayoutBlockColumnWidth(twigData: any): void {
        let contentWidth = this.focusedDistribution.generalStyles.distributionMaxWidthPx || 1200;

        const padding = twigData.styles.padding;
        if (padding) {
            const paddingWidth = padding.paddingOption ? padding.leftPadding + padding.rightPadding : padding.paddingAll * 2;
            contentWidth -= paddingWidth;
        }

        const columnSpacingWidth = twigData.styles.columnSpacing;
        if (columnSpacingWidth) {
            contentWidth -= columnSpacingWidth * (twigData.styles.width.length - 1);
        }

        twigData.styles.width = twigData.styles.width.map(width => {
            return contentWidth * parseInt(width, 10) / 100 + 'px';
        });
    }

    processMergeTags(body: string, mergeTags: MergeTagApiModel[]): string {
        let finalBody = '';

        let inMergeTag = false;
        let currentMergeTag = '';
        for (let i = 0; i < body?.length; i++) {
            if (body.charAt(i) === '[' && i < body.length - 1 && body.charAt(i + 1) === '%') {
                inMergeTag = true;
                i++;
                continue;
            }
            if (body.charAt(i) === '%' && i < body.length - 1 && body.charAt(i + 1) === ']') {
                inMergeTag = false;

                const completedMergeTag = this.parseMergeTag(currentMergeTag);

                if (currentMergeTag !== 'SUBSCRIPTION_COMMON_TRACKING_LINK') {
                    mergeTags.push(completedMergeTag);
                }

                currentMergeTag = '';

                // Strip out all the fallbacks from the body because DE cannot
                // interpret them.
                finalBody += ('[%' + completedMergeTag.key + '%]');
                i++;
                continue;
            }
            if (inMergeTag) {
                currentMergeTag += body.charAt(i);
            } else {
                finalBody += body.charAt(i);
            }
        }
        return finalBody;
    }

    parseMergeTag(mergeTagString: string): MergeTagApiModel {
        const topTag: MergeTagApiModel = {};
        let currentTag = topTag;
        let remainingTagString = mergeTagString;
        while (remainingTagString.indexOf('|') >= 0) {
            currentTag.key = remainingTagString.substring(0, remainingTagString.indexOf('|'));
            currentTag.fallback = {};
            currentTag = currentTag.fallback;
            remainingTagString = remainingTagString.substring(remainingTagString.indexOf('|') + 1);
        }
        currentTag.key = remainingTagString;

        return topTag;
    }


    renderFallbackTagsToDefault(body: string) {
        for (const tag in this.sampleTestEmailTagValues){
            if (this.sampleTestEmailTagValues.hasOwnProperty(tag)) {
                // Replacing all instances of tag
                body = body.split(tag).join(this.sampleTestEmailTagValues[tag]);
            }
        }
        return body;
    }

    /**
     * Returns the default template for the provided block type
     */
    getDefaultTemplateByBlockType(blockType: string, defaultReference: string): string {
        if (defaultReference === StyleStateEnum.DEFAULT || !defaultReference) {
            defaultReference = DistributionStyleStateEnum.DISTRIBUTION_1;
        }

        // If there is no template for the requested defaultReference, fall back to distribution_1.  Not all the defaultReferences need their own template
        return super.getDefaultTemplateByBlockType(blockType, defaultReference) ||
            super.getDefaultTemplateByBlockType(blockType, DistributionStyleStateEnum.DISTRIBUTION_1);
    }

    private presendRenderFooter(block: BlockModel, isTestEmail: boolean = false) {

        if (isTestEmail) {
            (block.displayElement as FooterDisplayElementModel).footer.company =
                (block.displayElement as FooterDisplayElementModel).footer.company
                || this.localizationService.transform('Discover.distribution.footer.test.companyPlaceholder');

            (block.displayElement as FooterDisplayElementModel).footer.city =
                (block.displayElement as FooterDisplayElementModel).footer.city
                || this.localizationService.transform('Discover.distribution.footer.test.cityPlaceholder');

            (block.displayElement as FooterDisplayElementModel).footer.streetAddress =
                (block.displayElement as FooterDisplayElementModel).footer.streetAddress
                || this.localizationService.transform('Discover.distribution.footer.test.addressPlaceholder');

            (block.displayElement as FooterDisplayElementModel).footer.postalCode =
                (block.displayElement as FooterDisplayElementModel).footer.postalCode
                || this.localizationService.transform('Discover.distribution.footer.test.postalCodePlaceholder');

            (block.displayElement as FooterDisplayElementModel).footer.country =
                (block.displayElement as FooterDisplayElementModel).footer.country
                || this.localizationService.transform('Discover.distribution.footer.test.countryPlaceholder');
        }

        const template = Twig.twig({
            data: block.type.twig
        });

        const footerDisplayElement = block.displayElement.clone() as FooterDisplayElementModel;
        footerDisplayElement.styles.maxWidthPx = this.focusedDistribution.generalStyles.distributionMaxWidthPx || 1200;
        footerDisplayElement.updateRenderedText(true);
        return template?.render({data: footerDisplayElement});
    }


    // TODO : May also need to override addBlock
    // Override to make sure the footer is always at the bottom
    addBlockOfType(type: BlockTypeModel, position?: string): void {
        const block = type.key === DistributionBlockTypeEnum.oneColumnLayout ||
            type.key === DistributionBlockTypeEnum.twoColumnLayout ||
            type.key === DistributionBlockTypeEnum.threeColumnLayout ?
            new LayoutBlock(null, type as LayoutBlockType, new type.displayElementType() as LayoutDisplayElement)
            : new BlockModel(null, type, new type.displayElementType());
        const distributionMaxWidthPx = this.focusedDistribution.generalStyles.distributionMaxWidthPx;
        if (distributionMaxWidthPx) {
            block.displayElement.styles['maxWidthPx'] = distributionMaxWidthPx;
        }
        if (type.key === DistributionBlockTypeEnum.text) {
            (block.displayElement as RichTextDisplayElementModel).enableAIHelper = true;
            (block.displayElement as RichTextDisplayElementModel).aiReferenceId = this.focusedDistribution.aiReferenceId;
        }
        this.applyGeneralStylesToBlock(block);

        if (!isNaN(+position) || !position) {
            const positionToAdd = !position ? null : +position;
            if ((!!positionToAdd || positionToAdd === 0 ) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                this.blocks.splice(positionToAdd, 0, block);
            } else {
                this.blocks.splice(this.blocks.length - 1, 0, block);
            }
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const layoutBlock = this.blocks[+blockPosition] as LayoutBlock;
            const column = layoutBlock.blocks[+columnIndex];
            const positionToAdd = +positionInColumn;
            if ((!!positionToAdd || positionToAdd === 0 ) && positionToAdd >= 0 && positionToAdd < column.length) {
                column.splice(positionToAdd, 0, block);
            } else {
                column.push(block);
            }
        }
        this.setBlocks(this.blocks);
        this.setFocusedBlockInfo({
            blockPosition: position,
            focusedBlockButtonType: null
        });
    }

    /**
     * Adds a block to the current list
     *
     * @param type A block type
     */
    addBlockAtPosition(block: BlockModel, position: string): void {
        if (!isNaN(+position)) {
            const positionToAdd = +position;
            if ((!!positionToAdd || positionToAdd === 0 ) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                this.blocks.splice(positionToAdd, 0, block);
            } else {
                this.blocks.splice(this.blocks.length - 1, 0, block);
            }
        } else {
            const indexParts = position.split('.');
            const blockPosition = +indexParts[0];
            const columnIndex = +indexParts[1];
            const positionInColumn = +indexParts[2];
            const layoutBlock = this.blocks[blockPosition] as LayoutBlock;
            const column = layoutBlock.blocks[columnIndex];
            const index = positionInColumn;
            column.splice(index, 0, block);
        }

        this.setBlocks(this.blocks);

        if (block.displayElement.styles.displayStyleState !== StyleStateEnum.CUSTOM) {
            this.applyGeneralStylesToBlock(block);
        }
    }

    removeBlock(block: BlockModel, position?: string): void {
        if (!isNaN(+position)) {
            const index = this.blocks.indexOf(block);
            if (index < 0) {
                // shouldn't ever happen
                throw new Error('Item not in list');
            }

            // If the deleted block is the selected block, unselect it
            if (this.selectedBlock && this.selectedBlock.displayElement?.isEqual(block.displayElement)) {
                this.deselectBlock(false);
            }
            this.blocks.splice(index, 1);
            this.setBlocks(this.blocks);
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const layoutBlock = this.blocks[+blockPosition] as LayoutBlock;
            const column = layoutBlock.blocks[+columnIndex];
            const positionToRemove = +positionInColumn;
            if (this.selectedBlock && this.selectedBlock.displayElement?.isEqual(layoutBlock.displayElement)) {
                this.deselectBlock(false);
            }
            column.splice(positionToRemove, 1);
            this.setBlocks(this.blocks);
            this.setFocusedBlockInfo({
                blockPosition: blockPosition + '.' + columnIndex + '.' + Math.max(0, positionToRemove - 1),
                focusedBlockButtonType: null
            });
        }
    }

    /**
     * Duplicate a block
     *
     * @param block The block to duplicate
     */
    duplicateBlock(block: BlockModel, position?: string): void {
        if (!isNaN(+position)) {
            const index = this.blocks.indexOf(block);
            if (index < 0) {
                // shouldn't ever happen
                throw new Error('Item not in list');
            }
            this.blocks.splice(index, 0, block.clone());
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const layoutBlock = this.blocks[+blockPosition] as LayoutBlock;
            const column = layoutBlock.blocks[+columnIndex];
            const positionToAdd = +positionInColumn;
            column.splice(positionToAdd, 0, block.clone());
        }

        this.setBlocks(this.blocks);
    }

    // Before-last block is not movable (down) because the footer must always be last
    protected updateMoveability(): void {
        if (this.blocks.length === 0) {
            return;
        }
        for (let i = 0, l = this.blocks.length; i < l; i++) {
            this.blocks[i].canMoveUp = true;
            this.blocks[i].canMoveUpIntoLayout = false;
            this.blocks[i].canMoveDown = true;
            this.blocks[i].canMoveDownIntoLayout = false;
            this.blocks[i].canMoveLeft = false;
            this.blocks[i].canMoveRight = false;
            if (this.blocks[i].isLayoutBlockType()) {
                const layoutBlock = this.blocks[i] as LayoutBlock;
                this.updateLayoutBlockMoveability(layoutBlock);
            } else {
                if (this.blocks[i - 1]?.isLayoutBlockType()) {
                    this.blocks[i].canMoveUpIntoLayout = true;
                }
                if (this.blocks[i + 1]?.isLayoutBlockType()) {
                    this.blocks[i].canMoveDownIntoLayout = true;
                }
            }
        }
        this.blocks[0].canMoveUp = false;
        if (this.blocks.length >= 2) {
            this.blocks[this.blocks.length - 2].canMoveDown = false;
        }
    }

    private updateLayoutBlockMoveability(layoutBlock: LayoutBlock): void {
        const columnLength = layoutBlock.blocks.length;
        layoutBlock.blocks.forEach((column, columnIndex) => {
            const blockLength = column.length;
            column.forEach((block, blockIndex) => {
                block.canMoveUp = true;
                block.canMoveDown = true;
                block.canMoveLeft = true;
                block.canMoveRight = true;
                if (blockIndex === 0) {
                    block.canMoveUp = false;
                }
                if (blockIndex === blockLength - 1) {
                    block.canMoveDown = false;
                }
                if (columnIndex === 0) {
                    block.canMoveLeft = false;
                }
                if (columnIndex === columnLength - 1) {
                    block.canMoveRight = false;
                }
            });
        });
    }

    moveBlock(block: BlockModel, direction: BlockMoveDirectionEnum, position: string, isToLayoutBlock: boolean = false) {
        const blocks = this.blocks;
        let focusedBlockPosition, focusedMoveButtonType;
        if (!isNaN(+position)) {
            const index = blocks.indexOf(block);
            if (index < 0) {
                // shouldn't ever happen
                throw new Error('Item not in list');
            }

            if (direction === BlockMoveDirectionEnum.up) {
                if (index === 0) {
                    // already at the top, can't move up more
                    return;
                }
                blocks.splice(index, 1);
                // focusedBlockPosition = index - 1;
                if (isToLayoutBlock) {
                    (blocks[index - 1] as LayoutBlock).blocks[0].push(block);
                    focusedBlockPosition = (index - 1) + '.0.' + ((blocks[index - 1] as LayoutBlock).blocks[0].length - 1);
                } else {
                    blocks.splice(index - 1, 0, block);
                    focusedBlockPosition = index - 1 + '';
                    focusedMoveButtonType = FocusedMoveButtonTypesEnum.up;
                }
            } else if (direction === BlockMoveDirectionEnum.down) {
                if (index === blocks.length - 1) {
                    // already at the bottom, can't move down more
                    return;
                }

                blocks.splice(index, 1);
                if (isToLayoutBlock) {
                    (blocks[index] as LayoutBlock).blocks[0].push(block);
                    focusedBlockPosition = index + '.0.' + ((blocks[index] as LayoutBlock).blocks[0].length - 1);
                } else {
                    blocks.splice(index + 1, 0, block);
                    focusedBlockPosition = index + 1 + '';
                    focusedMoveButtonType = FocusedMoveButtonTypesEnum.down;
                }
            }
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const layoutBlock = this.blocks[+blockPosition] as LayoutBlock;
            let column = layoutBlock.blocks[+columnIndex];
            let newColumnIndex;
            const index = +positionInColumn;
            column.splice(index, 1);
            switch(direction) {
                case BlockMoveDirectionEnum.up:
                    if (isToLayoutBlock) {
                        column.splice(index - 1, 0, block);
                        focusedBlockPosition = blockPosition + '.' + columnIndex + '.' + (index - 1);
                    } else {
                        blocks.splice(+blockPosition, 0, block);
                        focusedBlockPosition = blockPosition;
                        focusedMoveButtonType = FocusedMoveButtonTypesEnum.up;
                    }
                    break;
                case BlockMoveDirectionEnum.down:
                    if (isToLayoutBlock) {
                        column.splice(index + 1, 0, block);
                        focusedBlockPosition = blockPosition + '.' + columnIndex + '.' + (index + 1);
                    } else {
                        blocks.splice(+blockPosition + 1, 0, block);
                        focusedBlockPosition = (+blockPosition + 1) + '';
                        focusedMoveButtonType = FocusedMoveButtonTypesEnum.down;
                    }
                    break;
                case BlockMoveDirectionEnum.left:
                    newColumnIndex = +columnIndex - 1;
                    column = layoutBlock.blocks[newColumnIndex];
                    column.push(block);
                    focusedBlockPosition = blockPosition + '.' + newColumnIndex + '.' + (column.length - 1);
                    break;
                case BlockMoveDirectionEnum.right:
                    newColumnIndex = +columnIndex + 1;
                    column = layoutBlock.blocks[newColumnIndex];
                    column.push(block);
                    focusedBlockPosition = blockPosition + '.' + newColumnIndex + '.' + (column.length - 1);
                    break;
            }
        }

        this.setBlocks(blocks);

        this.setFocusedBlockInfo({
            blockPosition: focusedBlockPosition,
            focusedBlockButtonType: focusedMoveButtonType
        });
    }

    /**
     * While moveBlock is used for the move buttons, which limits to moving one block distance a time,
     * This method is used in drag and drop to move a block to an arbitrary position
     */
    moveBlockToPosition(block: BlockModel, position: string): void {
        const indexOfBlock = this.blocks.indexOf(block);
        if (indexOfBlock > -1) {
            this.blocks.splice(indexOfBlock, 1);
        } else {
            if (this.draggingBlockPosition) {
                const indexParts = this.draggingBlockPosition.split('.');
                const blockPosition = +indexParts[0];
                const columnIndex = +indexParts[1];
                const positionInColumn = +indexParts[2];
                const column = (this.blocks[blockPosition] as LayoutBlock).blocks[columnIndex];
                column.splice(positionInColumn, 1);
            } else {
                this.blocks.forEach((b, i) => {
                    if (!(b as any).blocks) {
                        return;
                    }
                    const layoutBlock = b as LayoutBlock;
                    layoutBlock.blocks.forEach(column => {
                        const index = column.indexOf(block);
                        if (index > -1) {
                            column.splice(index, 1);
                        }
                    });
                });
            }
        }
        if (isNaN(+position)) {
            const indexParts = position.split('.');
            let blockPosition = +indexParts[0];
            const columnIndex = +indexParts[1];
            const positionInColumn = +indexParts[2];
            if (indexOfBlock > -1 && indexOfBlock < blockPosition) {
                blockPosition--;
            }
            const layoutBlock = this.blocks[blockPosition] as LayoutBlock;
            const column = layoutBlock.blocks[columnIndex];
            const index = positionInColumn;
            column.splice(index, 0, block);
        }
        this.setBlocks(this.blocks);
    }

    setDefaultFooter() {
        this.cachedFooter = this.focusedDistribution.footer.clone();
        this.defaultFooterDirty$.next(false);
        this.distributionApi.setDefaultFooter(this.focusedDistribution.footer.toApiModel(null)).pipe(
            tap(() => this.userPreferencesService.refreshDistributionDefaultFooter())
        ).subscribe(obj => {
            this.messageService.add({
                severity : messageSeverityEnum.success,
                summary: this.localizationService.transform('Discover.distribution.footer.settings.setAsDefault.successMessageSummary'),
                detail: this.localizationService.transform('Discover.distribution.footer.settings.setAsDefault.successMessageDetail')
            });
        });
    }

    /**
     * Handles change events to generalStyles
     */
    generalStylesBlockUpdated(blockType: DistributionBlockTypeEnum, blockStyles: GenericStyleModel): void {
        for (const block of this.focusedDistribution.blocks) {
            if (blockType === block.type.key && block.displayElement.isBoundToGlobalStyles) {
                this.setPropertyValue(block.displayElement, 'styles', blockStyles.clone(block.displayElement.styles as GenericStyleModel));
            }
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        if (blockType === block.type.key && block.displayElement.isBoundToGlobalStyles) {
                            this.setPropertyValue(x.displayElement, 'styles', blockStyles.clone(x.displayElement.styles as GenericStyleModel));
                        };
                    });
                });
            }
        }

        if (this.selectedBlock) {
            this.selectedBlockUpdated();
        }
        this.setTemplatesOnBlocks(this.focusedDistribution.blocks);
        this.generalStylesProvider.next(this.generalStyles);
        this.focusedDistribution.generalStyles.setStyles(this.generalStyles);
        this.updateDirtyFlag();
    }

    blockFormUpdated(block: BlockModel, property: string, value: any): void {
        const isDefault: boolean = value &&
            (   value.styleState === StyleStateEnum.DEFAULT ||
                value.styleState === DistributionStyleStateEnum.DISTRIBUTION_1 ||
                value.styleState === DistributionStyleStateEnum.DISTRIBUTION_2 ||
                value.styleState === DistributionStyleStateEnum.DISTRIBUTION_3 ||
                value.styleState === DistributionStyleStateEnum.DISTRIBUTION_4 ||
                value.styleState === DistributionStyleStateEnum.DISTRIBUTION_5
            ) && value.displayStyleState !== DistributionStyleStateEnum.CUSTOM;

        if (value && value.hasOwnProperty('styleState') && isDefault) {
            this.updateContentBlockStyle(block, 'styles.styleState', value.styleState);
            this.setTemplatesOnBlocks(this.focusedDistribution.blocks);

            return;
        }

        if (property === 'isBoundToGlobalStyles' && value) {
            this.isBoundToGlobalStylesChange(block, property, value);

            return;
        }

        if (property !== 'styles') {
            block.errors = [];
            const emptyError = this.checkForEmptyBlock(block);
            if (emptyError) {
                block.errors.push(emptyError);
            }
            const invalidError = this.checkForInvalidBlock(block);
            if (invalidError) {
                block.errors.push(invalidError);
            }
            this.formFieldUpdated();

            return;
        }

        const generalStylesProperty = block.type.key;

        if (generalStylesProperty && block.displayElement.isBoundToGlobalStyles) {
            this.setPropertyValue(this.focusedDistribution.generalStyles, generalStylesProperty, block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, property, value);
        }

        this.formFieldUpdated();
        this.setTemplatesOnBlocks(this.focusedDistribution.blocks);
    }

    getDistributionById(id: number): Observable<DistributionApiModel> {
        return this.distributionApi.getById(id);
    }

    getDistributionByIds(ids: number[]): Observable<DistributionApiModel[]> {
        const apiCalls = [];
        [...new Set(ids)].forEach(id => {
            apiCalls.push(this.distributionApi.getById(id));
        });

        return combineLatest(apiCalls);
    }

    /**
     * Handles change to the selection for isBoundToGlobalStyles
     */
    isBoundToGlobalStylesChange(block: BlockModel, property: string, value: any) {
        if (!value) {
            this.formFieldUpdated();

            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            const generalStyleProperty = block.type.key;
            this.setPropertyValue(this.generalStyles, generalStyleProperty,  block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, 'styles', block.displayElement.styles.clone());
        }
        this.formFieldUpdated();
    }

    getDefaultStyle(blockTypeEnum: DistributionBlockTypeEnum, defaultReference?: DistributionStyleStateEnum) {
        if (defaultReference === DistributionStyleStateEnum.DEFAULT || !defaultReference) {
            defaultReference = DistributionStyleStateEnum.DISTRIBUTION_1;
        }

        const defaultValue = DistributionFactoryModel.defaultStyles[defaultReference][blockTypeEnum].clone();

        defaultValue.displayStyleState = defaultValue.styleState;

        return defaultValue;
    }

    blockEditingStateChanged(blockModel: BlockModel, isEditing: boolean, blockPosition: string): void {
        if (blockModel.type.key !== DistributionBlockTypeEnum.footer) {
            blockModel.canMove = !isEditing;
        }
        this.updateMoveability();
        this.updateDraggability(blockModel, isEditing, blockPosition);

        this.blockEditingState$.next(isEditing);
        this.blockDisplayElementChanged$.next({block: blockModel});
    }

    updateDraggability(blockModel: BlockModel, isEditing: boolean, blockPosition: string): void {
        const currentBlock = this.blocks[parseInt(blockPosition, 10)];
        if (currentBlock && currentBlock.isLayoutBlockType()) {
            currentBlock.draggable = !isEditing;
        }
    }

    public getDistributionPostOptions(id: number): Observable<AmplificationStatusAPIEnum[]> {
        return this.distributionApi.getDistributionPostOptions(id);
    }

    public setDistributionPostOptions(id: number, amplifications: AmplificationStatusAPIEnum[]) {
        return this.distributionApi.setDistributionPostOptions(id, amplifications);
    }

    /**
     * Returns the distribution benchmark statistics for the account
     */
    getAccountBenchmarks(): Observable<DistributionAccountStatisticsModel> {
        return this.getAccountStatsForDateRange(DateQueryModel.getLastXDaysQuery(90));
    }

    /**
     * Fetches account statistics for provided date range
     * */
    getAccountStatsForDateRange(dateQuery: DateQueryModel): Observable<DistributionAccountStatisticsModel> {
        const dateRange = this.getDateRangeForAccountStats(dateQuery);

        return this.distributionApi.getDistributionAccountStats(dateRange).pipe(mergeMap(apiModel => {
            return of(new DistributionAccountStatisticsModel(apiModel));
        }));
    }

    /**
     * Returns date range model for account stats request
     */
    getDateRangeForAccountStats(dateRange: DateQueryModel): DistributionAccountStatisticsDateRange {
        const dateQuery = dateRange;
        const hasDateQuery: boolean = dateQuery !== null && dateQuery !== undefined;
        let startDate: Date = null;
        let endDate: Date = null;

        if (hasDateQuery) {
            switch (dateQuery.type) {
                case DateQueryTypeEnum.ABSOLUTE:
                    startDate = dateQuery.absoluteRange.start;
                    endDate = dateQuery.absoluteRange.end;
                    break;
                case DateQueryTypeEnum.RELATIVE:
                    const momentPresent: moment.Moment = moment().utc();
                    const momentStartDate: moment.Moment = momentPresent.clone();
                    const momentEndDate: moment.Moment = momentPresent.clone();
                    momentStartDate.add({ 'hours': dateQuery.relativeStartOffsetH });
                    momentEndDate.add({ 'hours': dateQuery.relativeEndOffsetH });
                    startDate = momentStartDate.toDate();
                    endDate = momentEndDate.toDate();
                    break;
                case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                    startDate = DateQueryModel.getStartDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timezone).toDate();
                    endDate = DateQueryModel.getEndDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timezone).toDate();
                    break;
                default:
                    startDate = new Date(0);
                    endDate = new Date();
            }
        } else {
            startDate = new Date(0);
            endDate = new Date();
        }

        return {
            fromDate: startDate.toISOString(),
            toDate: endDate.toISOString()
        };
    }

    getResourceReports(ids: number[]):  Observable<DistributionRecipientReportApiModel[]> {
        const apiCalls = [];
        [...new Set(ids)].forEach(id => {
            apiCalls.push(this.distributionApi.getResourceReport(id));
        });

        return combineLatest(apiCalls) as Observable<DistributionRecipientReportApiModel[]>;
    }

    renderTemplate(twigTemplate: string, data: any): string {
        return EmailHelper.convertFontSizeUnitsToEM(this.twigRenderService.renderTemplate(twigTemplate, data), 14);
    }

    // TODO: Combine logic with cloneFromExistingDistribution
    copyDistribution(distribution: DistributionSummaryModel){
        const inputConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Discover.copyDistritbuion',
                initialValue: distribution.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: distribution.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                formValidator: (name: string) => {
                    if (this.distributionExistsWithName(null, name)) {
                        return [
                            'Discover.distribution.actions.errorMessageDuplicateName'
                        ];
                    } else {
                        return [];
                    }
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    this.distributionApi.getById(distribution.id).pipe(
                        mergeMap(_distribution => {
                            const distributionModel = new DistributionModel(_distribution);
                            distributionModel.deliverySchedule = new DeliverySchedule(
                                distributionModel.deliverySchedule.scheduleType === ScheduleType.immediate || distributionModel.sentNow ? null
                                    : distributionModel.deliverySchedule.scheduledDateUTC,
                                !!distributionModel.deliverySchedule.timeZone ? distributionModel.deliverySchedule.timeZone : null
                            );
                            distributionModel.name = name;
                            distributionModel.anonymize();
                            distributionModel.guid = null;
                            distributionModel.bodyPlainText = null;
                            distributionModel.footerPlainText = null;
                            distributionModel.groupId = group?.id;
                            distributionModel.isFavorite = false;
                            distributionModel.onlineBoostApproved = false;
                            return this.distributionApi.create(distributionModel.toApiModel()).pipe(
                                tap(() => this.setFocusedDistribution(distributionModel))
                            );
                        }),
                    ).subscribe({next: (x) => {
                        this.messageService.add({
                            severity : messageSeverityEnum.success,
                            detail: 'Discover.confirmDialog.copy.success',
                            summary: 'Discover.copyDistritbuion'
                        });
                        // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                        this.reloadDistributionSummaries();
                        this.discoverRoutingService.gotoDistribution(x);
                    }, error: (x) => this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Discover.copyDistritbuion',
                        detail: 'Discover.confirmDialog.copy.error'})
                    });

                    return of(null);
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, inputConfig);
    }

    private updateCachedDistribution(): void {
        this.cachedDistribution = JSON.parse(JSON.stringify(this.focusedDistribution));
        // JSON stringify will remove object methods, including equals, so need to add them back for dirty checking
        this.cachedDistribution.recipientInformation = this.focusedDistribution.recipientInformation;

        if (this.focusedDistribution.id) {
            this.distributionDirty = false;
            this.distributionDirty$.next(this.distributionDirty);
        }
    }

    uploadAttachments(attachments: FileUploadModel[], accessType: MediaServiceAccessTypeEnum = MediaServiceAccessTypeEnum.PRIVATE): Promise<FileUploadModel[]> {
        const filesAlreadyUploaded: FileUploadModel[] = attachments.filter(file => !!file.id);
        // All files without an id have yet to be uploaded
        const filesToUpload: File[] = attachments.filter(file => !file.id).map(file => file.file);

        if (filesToUpload.length === 0) {
            return Promise.resolve(attachments);
        }

        return lastValueFrom(
            this.mediaServiceApi.upload(filesToUpload, accessType).pipe(
                map(mediaResults => {
                    attachments = filesAlreadyUploaded;
                    mediaResults.mediaResults.forEach(result => {
                        if (!result.codedError) {
                            attachments.push({
                                id: result.media.id,
                                name: result.media.name,
                                size: result.media.size,
                                type: result.media.mimeType,
                                url: result.media.url
                            });
                        } else {
                            this.addUploadErrorMessage(result.media.name, result.codedError);
                        }
                    });
                    return attachments;
                })
            )
        );
    }

    addUploadErrorMessage(resultName, error) {
        let summaryMessage;
        let detailMessage;

        switch (error.code) {
            case MediaServiceErrorCodesEnum.invalidType:
                summaryMessage = 'Common.mediaService.fileUpload.typeError.heading';
                detailMessage = 'Common.mediaService.fileUpload.typeError.message';
                break;
            case MediaServiceErrorCodesEnum.fileMaxSize:
                summaryMessage = 'Common.mediaService.fileUpload.maxSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.maxSize.message';
                break;
            case MediaServiceErrorCodesEnum.insufficientSpace:
                summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                break;
            case MediaServiceErrorCodesEnum.fileMinSize:
                summaryMessage = 'Common.mediaService.fileUpload.minSize.heading';
                detailMessage = 'Common.mediaService.fileUpload.minSize.message';
                break;
            case MediaServiceErrorCodesEnum.duplicateFile:
                summaryMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.label';
                detailMessage = 'Common.lhp.libraryMoveDialog.upload.duplicate.message';
                break;
            case MediaServiceErrorCodesEnum.internalError:
            default:
                if (error.status === 429) {
                    summaryMessage = 'Common.mediaService.fileUpload.notEnoughSpace.heading';
                    detailMessage = 'Common.mediaService.fileUpload.notEnoughSpace.message';
                } else {
                    summaryMessage = 'Common.mediaService.fileUpload.internalError.heading';
                    detailMessage = 'Common.mediaService.fileUpload.internalError.message';
                }
                break;
        }

        this.messageService.add({
            severity : messageSeverityEnum.error,
            summary: this.localizationService.transform(summaryMessage),
            detail: this.localizationService.transform(detailMessage, { name: resultName })
        });
    }

    getAvailableMergeTags(): Observable<MergeTagModel[]> {
        return this.availableMergeTags$;
    }

    /** update the available merge tags with any fallback values */
    private setFallbackValues(mergeTags: MergeTagModel[]) {
        this.availableMergeTags.forEach(tag => {
            const matchingMergeTag = mergeTags?.find(x => x.key === tag.key);
            if (matchingMergeTag) {
                tag.fallback = matchingMergeTag.fallback;
            } else {
                tag.fallback = null;
            }
        });

        this.availableMergeTags$.next(this.availableMergeTags);
    }

    updateFallbackTags(mergeTags: MergeTagModel[], triggerSave: boolean = false) {
        this.setFallbackValues(mergeTags);
        this.focusedDistribution.mergeTags = mergeTags.filter(tag => tag.fallback?.key);
        if (triggerSave) {
            this.saveDistributionWithoutRefetch().subscribe();
        } else {
            this.focusedDistribution$.next(this.focusedDistribution);
            this.updateDirtyFlag();
        }
    }


    removedIndividualsFromList(targetingListId: number, query: TargetingQueryApi): Observable<void> {
        return this.targetingListsApiService.removeEntitiesMatchingSearch(targetingListId, query);
    }

    areLayoutBlocksAllowed(): boolean {
        return true;
    }

    isLayoutBlockType(type: string): boolean {
        return Object.values(LayoutBlockDisplayRatiosEnum).some(x => x as string === type);
    }

    getRecipientCount(distribution: DistributionSummaryModel): Observable<number> {
        // it's better to perform a recipient search using distribution reports endpoint for sent dist
        // because sent dist info is based on what we get from sendgrid and recipient calculation is its own thing
        if (distribution.status === DistributionStatus.sent || distribution.status === DistributionStatus.scheduled) {
            const recipientSearchModel = new RecipientSearchModel();
            recipientSearchModel.statType = DistributionReportStat.SENT;
            recipientSearchModel.query = new TargetingSearch().toApiModel().query;
            return this.distributionApi.performRecipientSearch(
                distribution.id,
                recipientSearchModel,
                0,
                0,
                SortOrderEnum.descending,
                SortFieldEnum.CIRCULATION_UNIQUE_VISITORS
            ).pipe(
                map(response => {
                    return response.numResults;
                })
            );
        } else {
            return this.distributionApi.getDistributionRecipientCount(distribution.id);
        }
    }

    setPendoCreateData(event: PendoEvent) {
        this.pendoCreateData = event;
    }

    headerUpdated(): void {
        if (this.focusedDistribution.sendFrom === null && !this.focusedDistribution.footer) {
            this.setFooterDefaultValues(this.focusedDistribution);
        }
        this.focusedDistribution$.next(this.focusedDistribution);
    }

    clearAEORecommendations(): void {
        this.AEORecommendations$.next([]);
    }

    getAEORecommendations(): Observable<AEORecommendationModel[]> {
        return this.AEORecommendations$;
    }

    getAEORecommendationsLoading(): Observable<boolean> {
        return this.AEORecommendationsLoading$;
    }

    generateAEORecommendations(): void {
        this.AEORecommendationsLoading$.next(true);
        this.distributionApi.getAEORecommendations(this.focusedDistribution.id).subscribe({
            next: (response: AEORecommendationsApiModel) => {
                if (response?.recommendations?.length >= 0) {
                    this.AEORecommendations$.next(response.recommendations);
                    this.AEORecommendationsLoading$.next(false);
                }
            },
            error: (error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Discover.aeoRecommendationsDialog.error'),
                    detail: this.localizationService.transform('Discover.aeoRecommendationsDialog.error.message')
                });
                this.AEORecommendationsLoading$.next(false);
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
